"use client";

import { forwardRef, useEffect, useImperativeHandle, useRef } from "react";

import nameMark from "../../assets/images/nametag.png";
import smashAtlasImage from "../../assets/images/smash-atlas.png";

// Smash animation constants (4x4 sprite atlas with 16 frames)
const SMASH_FRAME_COLUMNS = 4;
const SMASH_FRAME_ROWS = 4;
const SMASH_FRAME_COUNT = SMASH_FRAME_COLUMNS * SMASH_FRAME_ROWS;
const SMASH_FPS = 24;

const SplashCursor = forwardRef(function SplashCursor(
  {
    SIM_RESOLUTION = 128,
    DYE_RESOLUTION = 1440,
    CAPTURE_RESOLUTION = 512,
    DENSITY_DISSIPATION = 3.5,
    VELOCITY_DISSIPATION = 2,
    PRESSURE = 0.1,
    PRESSURE_ITERATIONS = 20,
    CURL = 3,
    SPLAT_RADIUS = 0.2,
    SPLAT_FORCE = 6000,
    SHADING = true,
    COLOR_UPDATE_SPEED = 0,
    BACK_COLOR = { r: 0.5, g: 0, b: 0 },
    TRANSPARENT = true,
    frontPalette = { base: [0.85, 0.9, 0.94], tint: [0.78, 0.84, 0.9] },
    backPalette = { base: [0.78, 0.84, 0.9], tint: [0.7, 0.76, 0.86] },
    frontSeed = 0,
    backSeed = 0,
    frontKind = 0,
    backKind = 0,
    frontTextEnabled = false,
    backTextEnabled = false,
    frontTextColor = [1, 1, 1],
    backTextColor = [1, 1, 1],
    surfaceBandStrength = 0.25,
    surfaceGrainStrength = 0.02,
    MAX_DEVICE_PIXEL_RATIO = 2,
    alphaMode = 0,
    enableClickSplat = false,
    className = "",
  },
  ref,
) {
  const canvasRef = useRef(null);
  const animationFrameId = useRef(null);
  const transitionRef = useRef({
    active: false,
    startTime: 0,
    duration: 0,
    center: { x: 0.5, y: 0.5 },
    maxRadius: 0,
    hold: false,
  });
  const transitionTriggerRef = useRef(null);
  const paletteRef = useRef({
    frontBase: frontPalette.base,
    frontTint: frontPalette.tint,
    backBase: backPalette.base,
    backTint: backPalette.tint,
    frontSeed,
    backSeed,
    frontKind,
    backKind,
    frontTextEnabled: Boolean(frontTextEnabled),
    backTextEnabled: Boolean(backTextEnabled),
    frontTextColor,
    backTextColor,
  });
  const alphaModeRef = useRef(alphaMode);
  const resetRef = useRef(null);
  const surfaceRef = useRef({
    bandStrength: surfaceBandStrength,
    grainStrength: surfaceGrainStrength,
  });

  useImperativeHandle(
    ref,
    () => ({
      triggerTransition: (options) => {
        transitionTriggerRef.current?.(options);
      },
      endTransition: () => {
        transitionRef.current.active = false;
        transitionRef.current.hold = false;
      },
      resetFluid: () => {
        resetRef.current?.();
      },
    }),
    [],
  );

  useEffect(() => {
    paletteRef.current = {
      frontBase: frontPalette.base,
      frontTint: frontPalette.tint,
      backBase: backPalette.base,
      backTint: backPalette.tint,
      frontSeed,
      backSeed,
      frontKind,
      backKind,
      frontTextEnabled: Boolean(frontTextEnabled),
      backTextEnabled: Boolean(backTextEnabled),
      frontTextColor,
      backTextColor,
    };
  }, [
    frontPalette,
    backPalette,
    frontSeed,
    backSeed,
    frontKind,
    backKind,
    frontTextEnabled,
    backTextEnabled,
    frontTextColor,
    backTextColor,
  ]);

  useEffect(() => {
    alphaModeRef.current = alphaMode;
  }, [alphaMode]);

  useEffect(() => {
    surfaceRef.current = {
      bandStrength: surfaceBandStrength,
      grainStrength: surfaceGrainStrength,
    };
  }, [surfaceBandStrength, surfaceGrainStrength]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    // Track if the effect is still active for cleanup
    let isActive = true;

    function pointerPrototype() {
      this.id = -1;
      this.texcoordX = 0;
      this.texcoordY = 0;
      this.prevTexcoordX = 0;
      this.prevTexcoordY = 0;
      this.deltaX = 0;
      this.deltaY = 0;
      this.down = false;
      this.moved = false;
      this.color = { r: 0, g: 0, b: 0 };
    }

    let config = {
      SIM_RESOLUTION,
      DYE_RESOLUTION,
      CAPTURE_RESOLUTION,
      DENSITY_DISSIPATION,
      VELOCITY_DISSIPATION,
      PRESSURE,
      PRESSURE_ITERATIONS,
      CURL,
      SPLAT_RADIUS,
      SPLAT_FORCE,
      SHADING,
      COLOR_UPDATE_SPEED,
      PAUSED: false,
      BACK_COLOR,
      TRANSPARENT,
    };

    let pointers = [new pointerPrototype()];

    const { gl, ext } = getWebGLContext(canvas);
    if (!ext.supportLinearFiltering) {
      config.DYE_RESOLUTION = 256;
      config.SHADING = false;
    }

    const nameMarkState = {
      texture: gl.createTexture(),
      ready: 0,
      aspect: 1,
    };

    if (nameMarkState.texture) {
      canvas.dataset.nameMarkReady = "0";
      gl.bindTexture(gl.TEXTURE_2D, nameMarkState.texture);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
      gl.texImage2D(
        gl.TEXTURE_2D,
        0,
        gl.RGBA,
        1,
        1,
        0,
        gl.RGBA,
        gl.UNSIGNED_BYTE,
        new Uint8Array([255, 255, 255, 0]),
      );

      const image = new Image();
      image.decoding = "async";
      image.src = nameMark;
      image.onload = () => {
        if (!isActive) return;
        nameMarkState.aspect = Math.max(
          0.01,
          image.naturalWidth / image.naturalHeight,
        );
        gl.bindTexture(gl.TEXTURE_2D, nameMarkState.texture);
        gl.pixelStorei(gl.UNPACK_FLIP_Y_WEBGL, true);
        gl.texImage2D(
          gl.TEXTURE_2D,
          0,
          gl.RGBA,
          gl.RGBA,
          gl.UNSIGNED_BYTE,
          image,
        );
        gl.pixelStorei(gl.UNPACK_FLIP_Y_WEBGL, false);
        nameMarkState.ready = 1;
        canvas.dataset.nameMarkReady = "1";
      };
      image.onerror = () => {
        nameMarkState.ready = -1;
        canvas.dataset.nameMarkReady = "-1";
        console.warn("[SplashCursor] nametag.png load failed");
      };
    }

    // Smash animation sprite atlas state
    const smashState = {
      texture: gl.createTexture(),
      ready: 0,
      aspect: 1,
      texelX: 1,
      texelY: 1,
    };

    if (smashState.texture) {
      canvas.dataset.smashReady = "0";
      gl.bindTexture(gl.TEXTURE_2D, smashState.texture);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
      gl.texImage2D(
        gl.TEXTURE_2D,
        0,
        gl.RGBA,
        1,
        1,
        0,
        gl.RGBA,
        gl.UNSIGNED_BYTE,
        new Uint8Array([0, 0, 0, 0]),
      );

      const smashImage = new Image();
      smashImage.decoding = "async";
      smashImage.src = smashAtlasImage;
      smashImage.onload = () => {
        if (!isActive) return;
        // Calculate aspect ratio of a single frame (atlas width/cols / atlas height/rows)
        const frameAspect =
          smashImage.naturalWidth /
          SMASH_FRAME_COLUMNS /
          (smashImage.naturalHeight / SMASH_FRAME_ROWS);
        smashState.aspect = Math.max(0.01, frameAspect);
        smashState.texelX = 1 / Math.max(1, smashImage.naturalWidth);
        smashState.texelY = 1 / Math.max(1, smashImage.naturalHeight);
        gl.bindTexture(gl.TEXTURE_2D, smashState.texture);
        gl.pixelStorei(gl.UNPACK_FLIP_Y_WEBGL, true);
        gl.texImage2D(
          gl.TEXTURE_2D,
          0,
          gl.RGBA,
          gl.RGBA,
          gl.UNSIGNED_BYTE,
          smashImage,
        );
        gl.pixelStorei(gl.UNPACK_FLIP_Y_WEBGL, false);
        smashState.ready = 1;
        canvas.dataset.smashReady = "1";
      };
      smashImage.onerror = () => {
        smashState.ready = -1;
        canvas.dataset.smashReady = "-1";
        console.warn("[SplashCursor] smash-atlas.png load failed");
      };
    }

    function getWebGLContext(canvas) {
      const params = {
        alpha: true,
        depth: false,
        stencil: false,
        antialias: false,
        preserveDrawingBuffer: false,
      };
      let gl = canvas.getContext("webgl2", params);
      const isWebGL2 = !!gl;
      if (!isWebGL2)
        gl =
          canvas.getContext("webgl", params) ||
          canvas.getContext("experimental-webgl", params);

      let halfFloat;
      let supportLinearFiltering;
      if (isWebGL2) {
        gl.getExtension("EXT_color_buffer_float");
        supportLinearFiltering = gl.getExtension("OES_texture_float_linear");
      } else {
        halfFloat = gl.getExtension("OES_texture_half_float");
        supportLinearFiltering = gl.getExtension(
          "OES_texture_half_float_linear",
        );
      }
      gl.clearColor(0.0, 0.0, 0.0, 1.0);

      const halfFloatTexType = isWebGL2
        ? gl.HALF_FLOAT
        : halfFloat && halfFloat.HALF_FLOAT_OES;
      let formatRGBA;
      let formatRG;
      let formatR;

      if (isWebGL2) {
        formatRGBA = getSupportedFormat(
          gl,
          gl.RGBA16F,
          gl.RGBA,
          halfFloatTexType,
        );
        formatRG = getSupportedFormat(gl, gl.RG16F, gl.RG, halfFloatTexType);
        formatR = getSupportedFormat(gl, gl.R16F, gl.RED, halfFloatTexType);
      } else {
        formatRGBA = getSupportedFormat(gl, gl.RGBA, gl.RGBA, halfFloatTexType);
        formatRG = getSupportedFormat(gl, gl.RGBA, gl.RGBA, halfFloatTexType);
        formatR = getSupportedFormat(gl, gl.RGBA, gl.RGBA, halfFloatTexType);
      }

      return {
        gl,
        ext: {
          formatRGBA,
          formatRG,
          formatR,
          halfFloatTexType,
          supportLinearFiltering,
        },
      };
    }

    function getSupportedFormat(gl, internalFormat, format, type) {
      if (!supportRenderTextureFormat(gl, internalFormat, format, type)) {
        switch (internalFormat) {
          case gl.R16F:
            return getSupportedFormat(gl, gl.RG16F, gl.RG, type);
          case gl.RG16F:
            return getSupportedFormat(gl, gl.RGBA16F, gl.RGBA, type);
          default:
            return null;
        }
      }
      return { internalFormat, format };
    }

    function supportRenderTextureFormat(gl, internalFormat, format, type) {
      const texture = gl.createTexture();
      gl.bindTexture(gl.TEXTURE_2D, texture);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.NEAREST);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.NEAREST);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
      gl.texImage2D(
        gl.TEXTURE_2D,
        0,
        internalFormat,
        4,
        4,
        0,
        format,
        type,
        null,
      );
      const fbo = gl.createFramebuffer();
      gl.bindFramebuffer(gl.FRAMEBUFFER, fbo);
      gl.framebufferTexture2D(
        gl.FRAMEBUFFER,
        gl.COLOR_ATTACHMENT0,
        gl.TEXTURE_2D,
        texture,
        0,
      );
      const status = gl.checkFramebufferStatus(gl.FRAMEBUFFER);
      return status === gl.FRAMEBUFFER_COMPLETE;
    }

    class Material {
      constructor(vertexShader, fragmentShaderSource) {
        this.vertexShader = vertexShader;
        this.fragmentShaderSource = fragmentShaderSource;
        this.programs = [];
        this.activeProgram = null;
        this.uniforms = [];
      }
      setKeywords(keywords) {
        let hash = 0;
        for (let i = 0; i < keywords.length; i++) hash += hashCode(keywords[i]);
        let program = this.programs[hash];
        if (program == null) {
          let fragmentShader = compileShader(
            gl.FRAGMENT_SHADER,
            this.fragmentShaderSource,
            keywords,
          );
          program = createProgram(this.vertexShader, fragmentShader);
          this.programs[hash] = program;
        }
        if (program === this.activeProgram) return;
        this.uniforms = getUniforms(program);
        this.activeProgram = program;
      }
      bind() {
        gl.useProgram(this.activeProgram);
      }
    }

    class Program {
      constructor(vertexShader, fragmentShader) {
        this.uniforms = {};
        this.program = createProgram(vertexShader, fragmentShader);
        this.uniforms = getUniforms(this.program);
      }
      bind() {
        gl.useProgram(this.program);
      }
    }

    function createProgram(vertexShader, fragmentShader) {
      let program = gl.createProgram();
      gl.attachShader(program, vertexShader);
      gl.attachShader(program, fragmentShader);
      gl.linkProgram(program);
      if (!gl.getProgramParameter(program, gl.LINK_STATUS))
        console.trace(gl.getProgramInfoLog(program));
      return program;
    }

    function getUniforms(program) {
      let uniforms = [];
      let uniformCount = gl.getProgramParameter(program, gl.ACTIVE_UNIFORMS);
      for (let i = 0; i < uniformCount; i++) {
        let uniformName = gl.getActiveUniform(program, i).name;
        uniforms[uniformName] = gl.getUniformLocation(program, uniformName);
      }
      return uniforms;
    }

    function compileShader(type, source, keywords) {
      source = addKeywords(source, keywords);
      const shader = gl.createShader(type);
      gl.shaderSource(shader, source);
      gl.compileShader(shader);
      if (!gl.getShaderParameter(shader, gl.COMPILE_STATUS))
        console.trace(gl.getShaderInfoLog(shader));
      return shader;
    }

    function addKeywords(source, keywords) {
      if (!keywords) return source;
      let keywordsString = "";
      keywords.forEach((keyword) => {
        keywordsString += "#define " + keyword + "\n";
      });
      return keywordsString + source;
    }

    const baseVertexShader = compileShader(
      gl.VERTEX_SHADER,
      `
        precision highp float;
        attribute vec2 aPosition;
        varying vec2 vUv;
        varying vec2 vL;
        varying vec2 vR;
        varying vec2 vT;
        varying vec2 vB;
        uniform vec2 texelSize;

        void main () {
            vUv = aPosition * 0.5 + 0.5;
            vL = vUv - vec2(texelSize.x, 0.0);
            vR = vUv + vec2(texelSize.x, 0.0);
            vT = vUv + vec2(0.0, texelSize.y);
            vB = vUv - vec2(0.0, texelSize.y);
            gl_Position = vec4(aPosition, 0.0, 1.0);
        }
      `,
    );

    const copyShader = compileShader(
      gl.FRAGMENT_SHADER,
      `
        precision mediump float;
        precision mediump sampler2D;
        varying highp vec2 vUv;
        uniform sampler2D uTexture;

        void main () {
            gl_FragColor = texture2D(uTexture, vUv);
        }
      `,
    );

    const clearShader = compileShader(
      gl.FRAGMENT_SHADER,
      `
        precision mediump float;
        precision mediump sampler2D;
        varying highp vec2 vUv;
        uniform sampler2D uTexture;
        uniform float value;

        void main () {
            gl_FragColor = value * texture2D(uTexture, vUv);
        }
      `,
    );

    const displayShaderSource = `
	      precision highp float;
	      precision highp sampler2D;
      varying vec2 vUv;
      varying vec2 vL;
      varying vec2 vR;
      varying vec2 vT;
      varying vec2 vB;
      uniform sampler2D uTexture;
      uniform vec2 texelSize;
      uniform vec3 uFrontBase;
      uniform vec3 uFrontTint;
      uniform vec3 uBackBase;
      uniform vec3 uBackTint;
      uniform float uFrontSeed;
      uniform float uBackSeed;
	      uniform float uFrontKind;
	      uniform float uBackKind;
	      uniform float uFrontTextEnabled;
	      uniform float uBackTextEnabled;
	      uniform vec3 uFrontTextColor;
	      uniform vec3 uBackTextColor;
	      uniform sampler2D uNameMark;
	      uniform float uNameMarkAspect;
	      uniform float uNameMarkReady;
	      uniform sampler2D uSmashAtlas;
	      uniform float uSmashAtlasAspect;
	      uniform float uSmashAtlasReady;
	      uniform vec2 uSmashAtlasTexel;
	      uniform float uSmashFrameCount;
	      uniform float uSmashFrameColumns;
	      uniform float uSmashFrameRows;
	      uniform float uSmashFps;
	      uniform float uTime;
	      uniform vec2 uResolution;
      uniform vec2 uTransitionCenter;
      uniform float uTransitionRadius;
      uniform float uTransitionActive;
      uniform float uAspect;
      uniform float uAlphaMode;
      uniform float uBandStrength;
      uniform float uGrainStrength;

      float maskFromColor(vec3 c) {
          return max(c.r, max(c.g, c.b));
      }

      float hash21(vec2 p) {
          p = fract(p * vec2(123.34, 456.21));
          p += dot(p, p + 34.345);
          return fract(p.x * p.y);
      }

      float random(vec2 st) {
          return fract(sin(dot(st.xy, vec2(12.9898, 78.233))) * 43758.5453123);
      }

      float vnoise(vec2 p) {
          vec2 i = floor(p);
          vec2 f = fract(p);
          float a = hash21(i);
          float b = hash21(i + vec2(1.0, 0.0));
          float c = hash21(i + vec2(0.0, 1.0));
          float d = hash21(i + vec2(1.0, 1.0));
          vec2 u = f * f * (3.0 - 2.0 * f);
          return mix(mix(a, b, u.x), mix(c, d, u.x), u.y);
      }

      float hash31(vec3 p) {
          return fract(sin(dot(p, vec3(127.1, 311.7, 74.7))) * 43758.5453123);
      }

      vec3 hash33(vec3 p) {
          return fract(
              sin(
                  vec3(
                      dot(p, vec3(127.1, 311.7, 74.7)),
                      dot(p, vec3(269.5, 183.3, 246.1)),
                      dot(p, vec3(113.5, 271.9, 124.6))
                  )
              ) * 43758.5453123
          );
      }

      float fbm(vec2 p) {
          float v = 0.0;
          float amp = 0.55;
          mat2 m = mat2(0.86, 0.5, -0.5, 0.86);
          for (int i = 0; i < 4; i += 1) {
              v += amp * vnoise(p);
              p = m * p * 2.02 + 11.3;
              amp *= 0.55;
          }
          return v;
      }

      vec3 surfaceLayer(vec2 uv, vec3 base, vec3 tint, float seed) {
          float band = smoothstep(0.45, 0.52, abs(sin((uv.y + uv.x * 0.12 + seed) * 18.0)));
          float grain = (random(uv * 120.0 + seed) - 0.5) * uGrainStrength;
          vec3 color = mix(base, tint, band * uBandStrength);
          color += grain;
          return clamp(color, 0.0, 1.0);
      }

	      float nameMarkMask(vec2 uv) {
	          if (uNameMarkReady < 0.5) return 0.0;
	          float markWidth = min(uResolution.x * 0.9, 1680.0);
          float markHeight = markWidth / max(uNameMarkAspect, 0.01);
          vec2 size = vec2(markWidth / max(uResolution.x, 1.0), markHeight / max(uResolution.y, 1.0));
          vec2 origin = vec2(0.5, 0.5) - size * 0.5;
          vec2 tuv = (uv - origin) / size;
	          if (tuv.x < 0.0 || tuv.x > 1.0 || tuv.y < 0.0 || tuv.y > 1.0) return 0.0;
	          return texture2D(uNameMark, tuv).a;
	      }

	      // Smash animation frame progress with smooth sinusoidal ping-pong
	      float smashFrameProgress(float frameCount, float fps, float time) {
	          float frames = max(1.0, floor(frameCount + 0.5));
	          if (frames <= 1.0) return 0.0;
	          float span = frames - 1.0;
	          // Use sinusoidal easing for smooth ping-pong without pauses at endpoints
	          float cycleDuration = span * 2.0 / max(fps, 1.0);
	          float phase = mod(time, cycleDuration) / cycleDuration;
	          // Smooth sine wave oscillation: 0 -> 1 -> 0 without any pause
	          float smoothT = 0.5 - 0.5 * cos(phase * 6.28318530718);
	          return smoothT * span;
	      }

	      // Sample a frame from the smash sprite atlas
	      float sampleSmashAtlasFrame(vec2 localUv, float frame, float cols, vec2 frameSize) {
	          float frameCol = mod(frame, cols);
	          float frameRow = floor(frame / cols);
	          // Keep linear filtering inside the current atlas tile
	          vec2 localInset = (uSmashAtlasTexel * 0.5) / frameSize;
	          vec2 safeUv = clamp(localUv, localInset, 1.0 - localInset);
	          vec2 atlasUv = (vec2(frameCol, frameRow) + safeUv) * frameSize;
	          return texture2D(uSmashAtlas, atlasUv).a;
	      }

	      // Get the smash animation mask at current time
	      float smashMask(vec2 uv) {
	          if (uSmashAtlasReady < 0.5) return 0.0;

	          float frames = max(1.0, floor(uSmashFrameCount + 0.5));
	          float cols = max(1.0, floor(uSmashFrameColumns + 0.5));
	          float rows = max(1.0, floor(uSmashFrameRows + 0.5));
	          vec2 frameSize = vec2(1.0 / cols, 1.0 / rows);

	          // Size: 50% of screen width, max 1000px - large and prominent
	          float artWidth = min(uResolution.x * 0.50, 1000.0);
	          float artHeight = artWidth / max(uSmashAtlasAspect, 0.01);
	          vec2 size = vec2(
	              artWidth / max(uResolution.x, 1.0),
	              artHeight / max(uResolution.y, 1.0)
	          );
	          // Positioned in lower half of screen (0.27 vertical, where 0=bottom, 1=top)
	          vec2 origin = vec2(0.5, 0.27) - size * 0.5;
	          vec2 localUv = (uv - origin) / size;

	          if (localUv.x < 0.0 || localUv.x > 1.0 || localUv.y < 0.0 || localUv.y > 1.0) {
	              return 0.0;
	          }

	          // Interpolate between frames for smooth animation
	          float frameProgress = smashFrameProgress(frames, uSmashFps, uTime);
	          float frame0 = clamp(floor(frameProgress), 0.0, frames - 1.0);
	          float frame1 = min(frames - 1.0, frame0 + 1.0);
	          float blendT = fract(frameProgress);
	          float a0 = sampleSmashAtlasFrame(localUv, frame0, cols, frameSize);
	          float a1 = sampleSmashAtlasFrame(localUv, frame1, cols, frameSize);
	          return mix(a0, a1, blendT);
	      }

	      float overlayMask(vec2 uv, float enabled) {
	          if (enabled < 0.5) return 0.0;
	          // Text mask only - smash is applied separately underneath
	          return nameMarkMask(uv);
	      }

	      vec3 applyText(vec3 col, vec2 uv, float enabled, vec3 textColor) {
	          if (enabled < 0.5) return col;
	          // First apply smash underneath (full brightness, same as text)
	          float smashA = smashMask(uv);
	          col = mix(col, textColor, smashA);
	          // Then apply text on top (full opacity)
	          float textA = nameMarkMask(uv);
	          return mix(col, textColor, textA);
	      }

      vec2 coverUv(vec2 uv, float imageAspect) {
          float canvasAspect = max(uAspect, 0.001);
          float imgAspect = max(imageAspect, 0.001);
          vec2 centered = uv - 0.5;
          if (canvasAspect > imgAspect) {
              float scale = canvasAspect / imgAspect;
              centered.x /= scale;
          } else {
              float scale = imgAspect / canvasAspect;
              centered.y /= scale;
          }
          return centered + 0.5;
      }

      float starContribution(vec2 cell, vec2 f, float time) {
          float n = hash21(cell);
          vec2 pos = vec2(hash21(cell + vec2(1.3, 7.1)), hash21(cell + vec2(9.2, 3.7))) - 0.5;
          float r = mix(0.02, 0.07, pow(n, 28.0));
          float d = length(f - pos * 0.85);
          float core = smoothstep(r, 0.0, d);
          float twinkle = 0.7 + 0.3 * sin(time * (0.8 + 2.7 * n) + n * 6.283);
          float glow = exp(-d * 18.0) * (0.15 + 0.25 * step(0.995, n));
          return core * twinkle + glow;
      }

      vec3 cometStreak(vec2 uv, float time, float seed) {
          // Single falling meteor (top -> bottom) to match the home StarryBackground vibe.
          float rate = 0.180;
          float timeline = time * rate + seed;
          float cycle = floor(timeline);
          float t = fract(timeline);

          float rx = hash21(vec2(seed + cycle * 13.7, 1.0));
          float ra = hash21(vec2(seed + cycle * 19.3, 2.0));

          // Always spawn from the top edge; randomize X and angle each cycle.
          vec2 start = vec2(mix(0.55, 1.10, rx), 1.18);

          // Angle range: mostly down with a slight left drift (in UV space where y decreases downward).
          float angle = mix(-1.72, -1.48, ra);
          vec2 dirUv = normalize(vec2(cos(angle), sin(angle)));
          vec2 end = start + dirUv * 2.2;

          vec2 pos = mix(start, end, t);

          // Compute distance in aspect-corrected space so the trail length stays consistent.
          vec2 puv = vec2((uv.x - 0.5) * uAspect, uv.y);
          vec2 ppos = vec2((pos.x - 0.5) * uAspect, pos.y);
          vec2 pstart = vec2((start.x - 0.5) * uAspect, start.y);
          vec2 pend = vec2((end.x - 0.5) * uAspect, end.y);

          vec2 dir = normalize(pend - pstart);
          vec2 d = puv - ppos;
          float along = dot(d, dir);
          float perp = length(d - dir * along);

          // Short trail that fades out from the tail as the meteor falls.
          float s = max(0.0, -along); // distance behind head along the meteor direction
          float trailLen = 0.255;
          float tailFeather = 0.035;

          // Constant-length tail window behind the head (with a soft cutoff at the end).
          float tailWindow = 1.0 - smoothstep(trailLen, trailLen + tailFeather, s);
          float behindMask = step(along, 0.0);
          float trailMask = behindMask * tailWindow;

          // Slight ramp so the trail starts just behind the head dot.
          trailMask *= smoothstep(0.0, 0.015, s);

          float trail = exp(-perp * 900.0) * trailMask * exp(-s * 1.4);
          float headStreak =
              exp(-perp * 1600.0) *
              trailMask *
              (1.0 - smoothstep(0.028, 0.06, s));

          // Bright head dot.
          float headDot = exp(-dot(d, d) * 3600.0);

          // Lifetime fade so it doesn't hang around.
          float fade = smoothstep(0.0, 0.08, t) * (1.0 - smoothstep(0.50, 0.70, t));

          vec3 cometStart = vec3(0.98, 0.99, 1.0);
          vec3 cometEnd = vec3(0.56, 0.84, 1.0);
          vec3 col = mix(cometStart, cometEnd, smoothstep(0.0, 1.0, t));

          float intensity = trail * 0.22 + headStreak * 0.18 + headDot * 0.70;
          return col * intensity * fade;
      }

      vec3 starryBackdrop(vec2 uv, vec3 base, vec3 tint, float seed, float time) {
          float aspect = uAspect;
          vec3 col = mix(base, tint, smoothstep(0.0, 1.0, uv.y));
          vec2 p = vec2((uv.x - 0.5) * aspect, uv.y - 0.5);

          // Match the StarryBackground haze: bottom glow + subtle blue lift.
          float radial = exp(-length(vec2(p.x, (uv.y - 1.15))) * 2.35);
          col += mix(tint, vec3(0.43, 0.73, 1.0), 0.65) * radial * 0.045;
          float band = smoothstep(0.58, 1.02, uv.y);
          col += mix(tint, vec3(0.28, 0.58, 1.0), 0.7) * band * 0.035;

          float hazeNoise = fbm(p * 0.85 + seed * 3.1 + time * 0.015);
          col += vec3(0.18, 0.34, 0.55) * hazeNoise * 0.012;

          vec2 grid = p * 58.0 + seed * 2.0;
          vec2 cell = floor(grid);
          vec2 f = fract(grid) - 0.5;
          float stars = 0.0;
          for (int j = 0; j < 3; j += 1) {
              for (int i = 0; i < 3; i += 1) {
                  vec2 offset = vec2(float(i - 1), float(j - 1));
                  float n = hash21(cell + offset);
                  float present = step(0.992, n);
                  stars += starContribution(cell + offset, f - offset, time) * present;
              }
          }
          stars = clamp(stars, 0.0, 1.25);
          vec3 starColor = mix(vec3(0.70, 0.82, 0.98), vec3(1.0), smoothstep(0.45, 1.0, stars));
          col += starColor * stars * 0.95;

          vec2 grid2 = p * 92.0 + seed * 5.0;
          vec2 cell2 = floor(grid2);
          vec2 f2 = fract(grid2) - 0.5;
          float stars2 = 0.0;
          for (int j = 0; j < 3; j += 1) {
              for (int i = 0; i < 3; i += 1) {
                  vec2 offset = vec2(float(i - 1), float(j - 1));
                  float n = hash21(cell2 + offset * 2.0);
                  float present = step(0.997, n);
                  stars2 += starContribution(cell2 + offset * 2.0, f2 - offset, time) * present;
              }
          }
          stars2 = clamp(stars2, 0.0, 1.0);
          col += vec3(0.82, 0.90, 1.0) * stars2 * 0.06;

          col += cometStreak(uv, time, seed * 0.21 + 0.12);

          float vignette = smoothstep(1.25, 0.38, length(p));
          col *= 0.86 + 0.14 * vignette;

          return clamp(col, 0.0, 1.0);
      }

      float snowflakeDist(vec2 p) {
          // Adapted from the Test 5 PixelSnow shader (snowflake variant).
          const float PI_OVER_6 = 0.5235988;
          const float PI_OVER_3 = 1.0471976;
          const vec2 b1d = vec2(0.574, 0.819);

          float r = length(p);
          float a = atan(p.y, p.x);
          a = abs(mod(a + PI_OVER_6, PI_OVER_3) - PI_OVER_6);
          vec2 q = r * vec2(cos(a), sin(a));

          float dMain = max(abs(q.y), max(-q.x, q.x - 1.0));
          float b1t = clamp(dot(q - vec2(0.4, 0.0), b1d), 0.0, 0.4);
          float dB1 = length(q - vec2(0.4, 0.0) - b1t * b1d);
          float b2t = clamp(dot(q - vec2(0.7, 0.0), b1d), 0.0, 0.25);
          float dB2 = length(q - vec2(0.7, 0.0) - b2t * b1d);
          return min(dMain, min(dB1, dB2)) * 10.0;
      }

      float pixelSnowFlake(vec2 uv, float time, float seed) {
          // WebGL1-friendly port of the PixelSnow (Testing 5) shader:
          // voxel traversal along a ray with snowflake silhouettes and a pixel grid.
          const vec3 camK = vec3(0.57735027, 0.57735027, 0.57735027);
          const vec3 camI = vec3(0.70710678, 0.0, -0.70710678);
          const vec3 camJ = vec3(-0.40824829, 0.81649658, -0.40824829);

          float pixelSize = max(1.0, floor(0.5 + uResolution.x / 330.0));
          vec2 fragCoord = floor(gl_FragCoord.xy / pixelSize) * pixelSize + pixelSize * 0.5;
          vec2 res = max(uResolution, vec2(1.0));
          vec2 centered = (fragCoord - res * 0.5) / res.x;

          vec3 ray = normalize(vec3(centered, 1.0));
          ray = ray.x * camI + ray.y * camJ + ray.z * camK;

          float direction = 2.1816616;
          float speed = 1.25;
          float timeSpeed = time * speed;
          float windX = cos(direction) * 0.4;
          float windY = sin(direction) * 0.4;
          vec3 camPos = (windX * camI + windY * camJ + 0.1 * camK) * timeSpeed;
          vec3 pos = camPos;

          vec3 absRay = max(abs(ray), vec3(0.001));
          vec3 strides = 1.0 / absRay;
          vec3 raySign = step(ray, vec3(0.0));
          vec3 phase = fract(pos) * strides;
          phase = mix(strides - phase, phase, raySign);

          float rayDotCamK = max(0.001, dot(ray, camK));
          float invRayDotCamK = 1.0 / rayDotCamK;
          float halfInvResX = 0.5 / max(res.x, 1.0);
          float timeAnim = timeSpeed * 0.08 + seed * 13.7;

          float density = 0.22;
          float baseFlakeSize = 0.017;
          float minFlakeSize = 1.25;
          float gamma = 0.4545;
          float brightness = 1.0;
          float farPlane = 10.0;

          float t = 0.0;
          for (int i = 0; i < 48; i += 1) {
              if (t >= farPlane) break;

              vec3 cell = floor(pos);
              vec3 h = hash33(cell + seed * 11.3);
              if (h.x < density) {
                  vec3 flakePos = cell + 0.1 + h * 0.8;
                  float wob = sin(timeAnim + h.x * 6.283);
                  flakePos += (h - 0.5) * wob * 0.18;

                  float toIntersection = dot(flakePos - pos, camK) * invRayDotCamK;
                  if (toIntersection > 0.0) {
                      vec3 testPos = pos + ray * toIntersection - flakePos;
                      float testX = dot(testPos, camI);
                      float testY = dot(testPos, camJ);

                      float depth = dot(flakePos - camPos, camK);
                      float flakeSize = max(baseFlakeSize, minFlakeSize * depth * halfInvResX);

                      float d = snowflakeDist(vec2(testX, testY) / max(flakeSize, 0.0001));
                      float inside = 1.0 - step(1.0, d);

                      if (inside > 0.5) {
                          float flakeSizeRatio = baseFlakeSize / flakeSize;
                          float intensity =
                              exp2(-(t + toIntersection)) *
                              min(1.0, flakeSizeRatio * flakeSizeRatio) *
                              brightness;

                          float value = clamp(pow(intensity, gamma), 0.0, 1.0);
                          if (value > 0.02) {
                              return value;
                          }
                          return 0.0;
                      }
                  }
              }

              float nextStep = min(min(phase.x, phase.y), phase.z);
              vec3 sel = step(phase, vec3(nextStep));
              phase = phase - nextStep + strides * sel;
              t += nextStep;
              pos = mix(pos + ray * nextStep, floor(pos + ray * nextStep + 0.5), sel);
          }

          return 0.0;
      }

      vec3 snowyBackdrop(vec2 uv, vec3 base, vec3 tint, float seed, float time) {
          vec3 col = mix(base, tint, smoothstep(0.0, 1.0, uv.y));

          float haze = exp(-length(vec2((uv.x - 0.5) * uAspect, (uv.y - 1.18))) * 2.2);
          col += vec3(0.38, 0.60, 0.95) * haze * 0.05;

          float cornerA = exp(-length(vec2((uv.x - 0.18) * uAspect, uv.y - 0.18)) * 2.1);
          float cornerB = exp(-length(vec2((uv.x - 0.82) * uAspect, uv.y - 0.06)) * 2.55);
          col += vec3(0.43, 0.73, 1.0) * cornerA * 0.04;
          col += vec3(0.86, 0.63, 1.0) * cornerB * 0.035;

          float snow = pixelSnowFlake(uv, time, seed);
          col = mix(col, vec3(1.0), snow);
          return col;
      }

      vec3 sunriseBackdrop(vec2 uv, float time) {
          float aspect = max(uAspect, 0.001);
          vec2 p = vec2((uv.x - 0.5) * aspect, uv.y - 0.5);

          // Rich multi-stop sky gradient matching the reference image
          vec3 deepPurple = vec3(0.12, 0.04, 0.24);
          vec3 midPurple  = vec3(0.28, 0.08, 0.38);
          vec3 warmMauve  = vec3(0.55, 0.18, 0.42);
          vec3 rosePink   = vec3(0.78, 0.32, 0.38);
          vec3 burntOrange= vec3(0.92, 0.48, 0.22);
          vec3 warmOrange = vec3(0.98, 0.58, 0.20);
          vec3 horizonGold= vec3(1.0, 0.72, 0.32);

          float skyGrad = uv.y;
          vec3 sky = horizonGold;
          sky = mix(sky, warmOrange, smoothstep(0.0, 0.12, skyGrad));
          sky = mix(sky, burntOrange, smoothstep(0.12, 0.22, skyGrad));
          sky = mix(sky, rosePink, smoothstep(0.22, 0.38, skyGrad));
          sky = mix(sky, warmMauve, smoothstep(0.38, 0.55, skyGrad));
          sky = mix(sky, midPurple, smoothstep(0.55, 0.75, skyGrad));
          sky = mix(sky, deepPurple, smoothstep(0.75, 1.0, skyGrad));

          // Subtle atmospheric noise to break up banding
          float atmoNoise = fbm(uv * 2.5 + vec2(time * 0.003, time * 0.002)) * 0.025;
          vec3 col = clamp(sky + atmoNoise, 0.0, 1.0);

          // Sun position: bottom center, half-visible
          vec2 sunPos = vec2(0.0, -0.48);
          float sunDist = length(p - sunPos);
          float sunRadius = 0.14;

          // Sun disc with warm bright core and soft limb darkening
          vec3 sunCore = vec3(1.0, 0.97, 0.88);
          vec3 sunLimb = vec3(1.0, 0.78, 0.42);
          float sunMask = smoothstep(sunRadius, sunRadius - 0.012, sunDist);
          float limbDark = smoothstep(0.0, sunRadius, sunDist);
          vec3 sunColor = mix(sunCore, sunLimb, limbDark * limbDark);
          col = mix(col, sunColor, sunMask);

          // Inner glow (warm halo tight around sun)
          vec3 innerGlowCol = vec3(1.0, 0.85, 0.48);
          float innerGlow = smoothstep(sunRadius + 0.22, sunRadius - 0.01, sunDist) * 0.55;
          col = mix(col, innerGlowCol, innerGlow * (1.0 - sunMask));

          // Mid glow (orange atmospheric haze)
          vec3 midGlowCol = vec3(0.98, 0.62, 0.28);
          float midGlow = smoothstep(sunRadius + 0.45, sunRadius, sunDist) * 0.32;
          col = mix(col, midGlowCol, midGlow * (1.0 - sunMask));

          // Outer atmospheric glow (pinkish)
          vec3 outerGlowCol = vec3(0.88, 0.45, 0.38);
          float outerGlow = smoothstep(sunRadius + 0.75, sunRadius + 0.1, sunDist) * 0.18;
          col = mix(col, outerGlowCol, outerGlow * (1.0 - sunMask));

          // Light rays emanating from sun (crepuscular rays)
          float angle = atan(p.y - sunPos.y, p.x - sunPos.x);
          float rayDist = sunDist;

          // Multiple ray frequencies for organic look
          float rays = 0.0;
          rays += sin(angle * 5.0 + 0.3) * 0.35;
          rays += sin(angle * 8.0 - 0.7) * 0.25;
          rays += sin(angle * 13.0 + 1.2) * 0.15;
          rays += sin(angle * 21.0 + time * 0.08) * 0.08;
          rays = max(0.0, rays);

          // Rays only above sun, fading with distance
          float rayMask = smoothstep(-0.02, 0.06, p.y - sunPos.y);
          float rayFade = exp(-rayDist * 2.2) * smoothstep(sunRadius * 0.6, sunRadius * 1.5, rayDist);
          float rayIntensity = rays * rayMask * rayFade * 0.65;

          vec3 rayColor = mix(vec3(1.0, 0.90, 0.60), vec3(1.0, 0.70, 0.45), smoothstep(0.0, 0.6, rayDist));
          col += rayColor * rayIntensity;

          // Subtle animated wispy high clouds
          float cloudY = uv.y;
          float cloudMask = smoothstep(0.35, 0.55, cloudY) * smoothstep(0.85, 0.65, cloudY);
          if (cloudMask > 0.0) {
              float cloud1 = fbm(vec2(uv.x * 3.0 + time * 0.008, uv.y * 1.5 + 0.3));
              float cloud2 = fbm(vec2(uv.x * 2.0 - time * 0.005, uv.y * 2.0 + 1.7));
              float clouds = smoothstep(0.45, 0.65, cloud1) * 0.12 + smoothstep(0.50, 0.70, cloud2) * 0.08;
              vec3 cloudColor = mix(vec3(0.72, 0.38, 0.48), vec3(0.55, 0.22, 0.42), cloudY);
              col = mix(col, cloudColor, clouds * cloudMask);
          }

          // Horizon glow band - warm light scattering at the horizon line
          float horizonBand = exp(-abs(uv.y - 0.08) * 18.0) * 0.25;
          col += vec3(1.0, 0.72, 0.35) * horizonBand;

          // Soft vignette
          float vignette = smoothstep(1.3, 0.4, length(p));
          col *= 0.88 + 0.12 * vignette;

          return clamp(col, 0.0, 1.0);
      }

      // ============================================
      // Beta Sky - anime clouds + soft sun
      // Fully bundled inside the layer render path so it reveal-matches.
      // ============================================
      float smin(float a, float b, float k) {
          float h = clamp(0.5 + 0.5 * (b - a) / k, 0.0, 1.0);
          return mix(b, a, h) - k * h * (1.0 - h);
      }

      float sdCircle(vec2 p, vec2 center, float radius) {
          return length(p - center) - radius;
      }

      float animeCloudSDF(vec2 p, float scale) {
          p /= scale;
          float k = 0.15;

          float d = sdCircle(p, vec2(0.0, 0.0), 0.25);
          d = smin(d, sdCircle(p, vec2(-0.2, 0.0), 0.23), k);
          d = smin(d, sdCircle(p, vec2(0.2, 0.0), 0.22), k);
          d = smin(d, sdCircle(p, vec2(-0.1, 0.18), 0.20), k);
          d = smin(d, sdCircle(p, vec2(0.12, 0.20), 0.19), k);
          d = smin(d, sdCircle(p, vec2(-0.28, 0.12), 0.17), k);
          d = smin(d, sdCircle(p, vec2(0.30, 0.10), 0.16), k);
          d = smin(d, sdCircle(p, vec2(0.0, 0.28), 0.14), k);
          d = smin(d, sdCircle(p, vec2(-0.18, 0.26), 0.12), k);
          d = smin(d, sdCircle(p, vec2(0.20, 0.25), 0.11), k);
          d = smin(d, sdCircle(p, vec2(-0.38, 0.0), 0.15), k);
          d = smin(d, sdCircle(p, vec2(0.40, 0.0), 0.14), k);
          d = smin(d, sdCircle(p, vec2(-0.12, -0.12), 0.16), k);
          d = smin(d, sdCircle(p, vec2(0.10, -0.14), 0.15), k);
          d = smin(d, sdCircle(p, vec2(-0.30, -0.08), 0.12), k);
          d = smin(d, sdCircle(p, vec2(0.28, -0.10), 0.13), k);

          return d * scale;
      }

      float animeCloudSDF2(vec2 p, float scale) {
          p /= scale;
          float k = 0.14;

          float d = sdCircle(p, vec2(0.0, 0.0), 0.22);
          d = smin(d, sdCircle(p, vec2(-0.22, 0.0), 0.20), k);
          d = smin(d, sdCircle(p, vec2(0.24, 0.0), 0.19), k);
          d = smin(d, sdCircle(p, vec2(-0.40, 0.0), 0.15), k);
          d = smin(d, sdCircle(p, vec2(0.42, 0.0), 0.14), k);
          d = smin(d, sdCircle(p, vec2(-0.08, 0.16), 0.18), k);
          d = smin(d, sdCircle(p, vec2(0.14, 0.18), 0.17), k);
          d = smin(d, sdCircle(p, vec2(-0.26, 0.12), 0.14), k);
          d = smin(d, sdCircle(p, vec2(0.30, 0.10), 0.13), k);
          d = smin(d, sdCircle(p, vec2(0.0, 0.24), 0.12), k);
          d = smin(d, sdCircle(p, vec2(-0.52, 0.02), 0.10), k);
          d = smin(d, sdCircle(p, vec2(0.54, 0.0), 0.09), k);
          d = smin(d, sdCircle(p, vec2(-0.10, -0.10), 0.14), k);
          d = smin(d, sdCircle(p, vec2(0.12, -0.12), 0.13), k);
          d = smin(d, sdCircle(p, vec2(-0.32, -0.06), 0.10), k);
          d = smin(d, sdCircle(p, vec2(0.34, -0.08), 0.11), k);

          return d * scale;
      }

      float animeCloudSDF3(vec2 p, float scale) {
          p /= scale;
          float k = 0.12;

          float d = sdCircle(p, vec2(0.0, 0.0), 0.18);
          d = smin(d, sdCircle(p, vec2(-0.16, 0.02), 0.15), k);
          d = smin(d, sdCircle(p, vec2(0.17, 0.0), 0.14), k);
          d = smin(d, sdCircle(p, vec2(-0.06, 0.14), 0.13), k);
          d = smin(d, sdCircle(p, vec2(0.08, 0.15), 0.12), k);
          d = smin(d, sdCircle(p, vec2(-0.20, 0.10), 0.10), k);
          d = smin(d, sdCircle(p, vec2(0.22, 0.08), 0.09), k);
          d = smin(d, sdCircle(p, vec2(-0.28, 0.0), 0.09), k);
          d = smin(d, sdCircle(p, vec2(0.30, 0.0), 0.08), k);
          d = smin(d, sdCircle(p, vec2(0.0, -0.10), 0.12), k);
          d = smin(d, sdCircle(p, vec2(-0.14, -0.08), 0.10), k);
          d = smin(d, sdCircle(p, vec2(0.12, -0.09), 0.09), k);

          return d * scale;
      }

      const vec2 CLOUD_EXTENT_A = vec2(0.62, 0.48);
      const vec2 CLOUD_EXTENT_B = vec2(0.68, 0.40);
      const vec2 CLOUD_EXTENT_C = vec2(0.42, 0.30);
      const float CLOUD_BOUNDS_PADDING = 0.06;
      const vec2 STORM_LIGHT_DIR = vec2(-0.4472136, 0.8944272);

      bool cloudInBounds(vec2 p, vec2 center, float scale, vec2 halfExtent, float padding) {
          vec2 delta = abs(p - center);
          vec2 bounds = halfExtent * scale + vec2(padding);
          return delta.x <= bounds.x && delta.y <= bounds.y;
      }

      vec4 shadeAnimeCloud(float sdf, vec2 p, vec2 cloudCenter, float scale, float lightAngle) {
          if (sdf > 0.01) return vec4(0.0);

          vec2 localP = (p - cloudCenter) / scale;

          vec3 highlight = vec3(1.0, 1.0, 1.0);
          vec3 midtone = vec3(0.95, 0.97, 1.0);
          vec3 shadow = vec3(0.82, 0.88, 0.96);
          vec3 deepShadow = vec3(0.72, 0.80, 0.92);

          vec2 lightDir = vec2(cos(lightAngle), sin(lightAngle));
          float lightFactor = dot(localP, lightDir) * 0.5 + 0.5;
          float verticalShade = smoothstep(-0.15, 0.25, localP.y);
          float shade = mix(lightFactor, verticalShade, 0.6);

          shade =
              smoothstep(0.0, 0.3, shade) * 0.4 +
              smoothstep(0.3, 0.6, shade) * 0.35 +
              smoothstep(0.6, 1.0, shade) * 0.25;

          vec3 color = mix(deepShadow, shadow, smoothstep(0.0, 0.35, shade));
          color = mix(color, midtone, smoothstep(0.35, 0.65, shade));
          color = mix(color, highlight, smoothstep(0.65, 1.0, shade));

          float topHighlight =
              smoothstep(0.1, 0.25, localP.y) *
              (1.0 - smoothstep(0.25, 0.35, localP.y));
          color = mix(color, vec3(1.0), topHighlight * 0.3);

          float rim = smoothstep(-0.02, 0.0, sdf);
          color = mix(color, vec3(1.0, 1.0, 0.98), rim * 0.4);

          float alpha = smoothstep(0.01, -0.02, sdf);
          alpha *= smoothstep(0.005, -0.015, sdf);

          return vec4(color, alpha);
      }

      vec4 shadeAnimeCloudLit(float sdf, vec2 p, vec2 cloudCenter, float scale, float lightAngle, vec2 sunPos) {
          if (sdf > 0.01) return vec4(0.0);

          vec2 localP = (p - cloudCenter) / scale;

          vec3 highlight = vec3(1.0, 1.0, 1.0);
          vec3 midtone = vec3(0.95, 0.97, 1.0);
          vec3 shadow = vec3(0.82, 0.88, 0.96);
          vec3 deepShadow = vec3(0.72, 0.80, 0.92);

          vec2 lightDir = vec2(cos(lightAngle), sin(lightAngle));
          float lightFactor = dot(localP, lightDir) * 0.5 + 0.5;
          float verticalShade = smoothstep(-0.15, 0.25, localP.y);
          float shade = mix(lightFactor, verticalShade, 0.6);

          shade =
              smoothstep(0.0, 0.3, shade) * 0.4 +
              smoothstep(0.3, 0.6, shade) * 0.35 +
              smoothstep(0.6, 1.0, shade) * 0.25;

          vec3 color = mix(deepShadow, shadow, smoothstep(0.0, 0.35, shade));
          color = mix(color, midtone, smoothstep(0.35, 0.65, shade));
          color = mix(color, highlight, smoothstep(0.65, 1.0, shade));

          float topHighlight =
              smoothstep(0.1, 0.25, localP.y) *
              (1.0 - smoothstep(0.25, 0.35, localP.y));
          color = mix(color, vec3(1.0), topHighlight * 0.3);

          // Sun-facing rim light: clouds near the sun get a warm golden edge
          float cloudSunDist = length(cloudCenter - sunPos);
          float sunProximity = smoothstep(0.8, 0.15, cloudSunDist);
          vec3 warmRim = vec3(1.0, 0.95, 0.82);
          float rim = smoothstep(-0.02, 0.0, sdf);
          color = mix(color, warmRim, rim * (0.4 + sunProximity * 0.35));

          float alpha = smoothstep(0.01, -0.02, sdf);
          alpha *= smoothstep(0.005, -0.015, sdf);

          return vec4(color, alpha);
      }

      vec3 betaSkyBackdrop(vec2 uv, vec3 base, vec3 tint, float time, float seed) {
          float aspect = max(uAspect, 0.001);
          vec2 p = vec2((uv.x - 0.5) * aspect, uv.y - 0.5);

          // Richer sky gradient with more color stops
          vec3 skyZenith = base * 0.92;
          vec3 skyUpper = mix(base, vec3(0.45, 0.68, 0.95), 0.3);
          vec3 skyMid = mix(tint, vec3(0.72, 0.88, 0.98), 0.4);
          vec3 skyHorizon = mix(tint, vec3(0.88, 0.94, 0.99), 0.5);
          vec3 skyWarm = vec3(0.95, 0.92, 0.88);

          float skyGrad = uv.y;
          vec3 sky = mix(skyWarm, skyHorizon, smoothstep(0.0, 0.08, skyGrad));
          sky = mix(sky, skyMid, smoothstep(0.08, 0.35, skyGrad));
          sky = mix(sky, skyUpper, smoothstep(0.35, 0.7, skyGrad));
          sky = mix(sky, skyZenith, smoothstep(0.7, 1.0, skyGrad));

          // Subtle sky noise to break banding
          float skyNoise = fbm(uv * 2.0 + vec2(seed * 0.1, time * 0.002)) * 0.015;
          vec3 col = clamp(sky + skyNoise, 0.0, 1.0);

          // Sun - positioned at upper left with warm glow
          float sunRadius = 0.10;
          float sunInset = 0.06;
          vec2 sunPos = vec2(-0.5 * aspect + sunRadius + sunInset, 0.28);
          float sunDist = length(p - sunPos);

          // Atmospheric glow layers (applied before sun disc)
          vec3 farGlowCol = vec3(0.98, 0.95, 0.90);
          float farGlow = smoothstep(sunRadius + 0.7, sunRadius + 0.05, sunDist) * 0.12;
          col = mix(col, farGlowCol, farGlow);

          vec3 outerGlowCol = vec3(1.0, 0.92, 0.72);
          float outerGlow = smoothstep(sunRadius + 0.40, sunRadius, sunDist) * 0.28;
          col = mix(col, outerGlowCol, outerGlow);

          vec3 innerGlowCol = vec3(1.0, 0.96, 0.80);
          float innerGlow = smoothstep(sunRadius + 0.18, sunRadius, sunDist) * 0.55;
          col = mix(col, innerGlowCol, innerGlow);

          // Sun disc with limb darkening
          vec3 sunCore = vec3(1.0, 0.99, 0.94);
          vec3 sunEdge = vec3(1.0, 0.92, 0.72);
          float sunMask = smoothstep(sunRadius, sunRadius - 0.008, sunDist);
          float limbT = sunDist / max(sunRadius, 0.001);
          vec3 sunColor = mix(sunCore, sunEdge, limbT * limbT);
          col = mix(col, sunColor, sunMask);

          // Soft god rays
          float angle = atan(p.y - sunPos.y, p.x - sunPos.x);
          float rayNoise = 0.0;
          rayNoise += sin(angle * 6.0 + 0.5) * 0.30;
          rayNoise += sin(angle * 10.0 - 1.2) * 0.20;
          rayNoise += sin(angle * 16.0 + time * 0.06) * 0.10;
          rayNoise = max(0.0, rayNoise);
          float rayFade = exp(-sunDist * 3.5) * smoothstep(sunRadius * 0.5, sunRadius * 1.8, sunDist);
          col += vec3(1.0, 0.96, 0.84) * rayNoise * rayFade * 0.18;

          // Cloud drift
          float drift = time * 0.015 + seed * 0.017;

          // Layer 1 - large background cloud (high, slow)
          float cloudX1 = mod(1.2 - drift * 0.7, 3.0) - 1.0;
          vec2 cloudPos1 = vec2(cloudX1, 0.20);
          if (cloudInBounds(p, cloudPos1, 0.58, CLOUD_EXTENT_A, CLOUD_BOUNDS_PADDING)) {
              float sdf1 = animeCloudSDF(p - cloudPos1, 0.58);
              vec4 cloud1 = shadeAnimeCloudLit(sdf1, p, cloudPos1, 0.58, 2.4, sunPos);
              col = mix(col, cloud1.rgb, cloud1.a * 0.92);
          }

          // Layer 2 - mid cloud
          float cloudX2 = mod(0.5 - drift * 1.0, 3.2) - 1.0;
          vec2 cloudPos2 = vec2(cloudX2, 0.04);
          if (cloudInBounds(p, cloudPos2, 0.48, CLOUD_EXTENT_B, CLOUD_BOUNDS_PADDING)) {
              float sdf2 = animeCloudSDF2(p - cloudPos2, 0.48);
              vec4 cloud2 = shadeAnimeCloudLit(sdf2, p, cloudPos2, 0.48, 2.2, sunPos);
              col = mix(col, cloud2.rgb, cloud2.a * 0.96);
          }

          // Layer 3 - lower foreground cloud
          float cloudX3 = mod(-0.3 - drift * 1.3, 2.8) - 0.9;
          vec2 cloudPos3 = vec2(cloudX3, -0.12);
          if (cloudInBounds(p, cloudPos3, 0.32, CLOUD_EXTENT_C, CLOUD_BOUNDS_PADDING)) {
              float sdf3 = animeCloudSDF3(p - cloudPos3, 0.32);
              vec4 cloud3 = shadeAnimeCloudLit(sdf3, p, cloudPos3, 0.32, 2.5, sunPos);
              col = mix(col, cloud3.rgb, cloud3.a * 0.97);
          }

          // Layer 4 - high accent cloud
          float cloudX4 = mod(2.0 - drift * 0.85, 3.4) - 1.1;
          vec2 cloudPos4 = vec2(cloudX4, 0.28);
          if (cloudInBounds(p, cloudPos4, 0.45, CLOUD_EXTENT_A, CLOUD_BOUNDS_PADDING)) {
              float sdf4 = animeCloudSDF(p - cloudPos4, 0.45);
              vec4 cloud4 = shadeAnimeCloudLit(sdf4, p, cloudPos4, 0.45, 2.3, sunPos);
              col = mix(col, cloud4.rgb, cloud4.a * 0.90);
          }

          // Layer 5 - small wispy puff
          float cloudX5 = mod(0.8 - drift * 0.55, 3.0) - 0.9;
          vec2 cloudPos5 = vec2(cloudX5, 0.34);
          if (cloudInBounds(p, cloudPos5, 0.22, CLOUD_EXTENT_C, CLOUD_BOUNDS_PADDING)) {
              float sdf5 = animeCloudSDF3(p - cloudPos5, 0.22);
              vec4 cloud5 = shadeAnimeCloudLit(sdf5, p, cloudPos5, 0.22, 2.4, sunPos);
              col = mix(col, cloud5.rgb, cloud5.a * 0.65);
          }

          // Layer 6 - far-away tiny puff at horizon
          float cloudX6 = mod(-0.8 - drift * 0.45, 2.6) - 0.8;
          vec2 cloudPos6 = vec2(cloudX6, -0.22);
          if (cloudInBounds(p, cloudPos6, 0.18, CLOUD_EXTENT_C, CLOUD_BOUNDS_PADDING)) {
              float sdf6 = animeCloudSDF3(p - cloudPos6, 0.18);
              vec4 cloud6 = shadeAnimeCloudLit(sdf6, p, cloudPos6, 0.18, 2.5, sunPos);
              col = mix(col, cloud6.rgb, cloud6.a * 0.55);
          }

          return clamp(col, 0.0, 1.0);
      }

      vec4 shadeStormCloud(float sdf, vec2 p, vec2 cloudCenter, float scale, float time, float seed) {
          if (sdf > 0.03) return vec4(0.0);

          vec2 localP = (p - cloudCenter) / scale;

          float edgeN = fbm(localP * 3.2 + vec2(seed * 2.1, seed * 3.7) + time * 0.02);
          float sdfN = sdf + (edgeN - 0.5) * (0.03 * scale);
          if (sdfN > 0.01) return vec4(0.0);

          // Darker, more dramatic 5-band palette
          vec3 highlight = vec3(0.82, 0.85, 0.90);
          vec3 lightGrey = vec3(0.60, 0.64, 0.72);
          vec3 midtone   = vec3(0.40, 0.44, 0.54);
          vec3 shadow    = vec3(0.24, 0.27, 0.36);
          vec3 core      = vec3(0.12, 0.13, 0.20);
          vec3 outlineCol = vec3(0.09, 0.10, 0.16);

          float lightDot = dot(localP, STORM_LIGHT_DIR) * 0.5 + 0.5;
          float verticalGrad = smoothstep(-0.35, 0.40, localP.y);
          float radialDist = length(localP);
          float radialFade = 1.0 - smoothstep(0.0, 0.55, radialDist);

          float shade = verticalGrad * 0.58 + lightDot * 0.30 + radialFade * 0.12;

          float tex = fbm(localP * 5.0 + vec2(seed * 1.3, seed * 2.5) - time * 0.03);
          float texChunk = floor(tex * 5.0) / 5.0;
          float streaks = smoothstep(0.25, 0.85, vnoise(localP * vec2(1.6, 8.0) + seed));
          shade = clamp(shade + (texChunk - 0.5) * 0.26 - streaks * 0.06, 0.0, 1.0);

          // 5-band quantization for more dramatic contrast
          float banded = floor(shade * 5.0) / 4.0;
          shade = mix(shade, banded, 0.75);

          vec3 color;
          if (shade < 0.25) {
              color = mix(core, shadow, shade / 0.25);
          } else if (shade < 0.50) {
              color = mix(shadow, midtone, (shade - 0.25) / 0.25);
          } else if (shade < 0.75) {
              color = mix(midtone, lightGrey, (shade - 0.50) / 0.25);
          } else {
              color = mix(lightGrey, highlight, (shade - 0.75) / 0.25);
          }

          // Slight green-grey storm tint
          color = mix(color, color * vec3(0.92, 0.96, 0.90), 0.25);

          float topSheen = smoothstep(0.1, 0.38, localP.y) * smoothstep(0.55, 0.15, radialDist);
          color = mix(color, highlight, topSheen * 0.45);

          // Underside darkness (storm belly)
          float bellyDark = smoothstep(0.05, -0.25, localP.y) * smoothstep(0.55, 0.10, radialDist);
          color = mix(color, core, bellyDark * 0.45);

          float edgeDist = -sdfN / scale;
          float rimLight = smoothstep(0.0, 0.04, edgeDist) * smoothstep(0.10, 0.05, edgeDist);
          rimLight *= smoothstep(-0.1, 0.2, localP.y);
          color = mix(color, vec3(0.85, 0.88, 0.92), rimLight * 0.30);

          // Lightning flash on cloud undersides - intermittent bright pulse
          float flashT = time * 1.8 + seed * 7.3;
          float flashCycle = fract(flashT);
          float flashBurst = smoothstep(0.0, 0.02, flashCycle) * smoothstep(0.06, 0.03, flashCycle);
          flashBurst += smoothstep(0.08, 0.10, flashCycle) * smoothstep(0.14, 0.11, flashCycle) * 0.6;
          float flashGate = step(0.88, fract(sin(floor(flashT) * 43758.5453) * 0.5 + 0.5));
          float flashMask = bellyDark * flashGate * flashBurst;
          color = mix(color, vec3(0.92, 0.90, 1.0), flashMask * 0.7);

          float outlineW = 0.03;
          float outlineMask = smoothstep(outlineW, outlineW * 0.35, abs(sdfN) / scale);
          color = mix(outlineCol, color, outlineMask);

          float alpha = smoothstep(0.02, -0.02, sdfN);
          alpha = clamp(alpha * 1.05, 0.0, 1.0);

          return vec4(color, alpha);
      }

      vec3 rainStreak(vec2 uv, float time, float seed) {
          // Thin rain streaks falling nearly vertically.
          float rate = 1.65;
          float timeline = time * rate + seed;
          float cycle = floor(timeline);
          float t = fract(timeline);

          float rx = hash21(vec2(seed + cycle * 13.7, 1.0));
          float ra = hash21(vec2(seed + cycle * 19.3, 2.0));

          vec2 start = vec2(mix(-0.08, 1.08, rx), 1.22);
          float angle = mix(-1.63, -1.51, ra);
          vec2 dirUv = normalize(vec2(cos(angle), sin(angle)));
          vec2 end = start + dirUv * 2.6;

          vec2 pos = mix(start, end, t);

          vec2 puv = vec2((uv.x - 0.5) * uAspect, uv.y);
          vec2 ppos = vec2((pos.x - 0.5) * uAspect, pos.y);
          vec2 pstart = vec2((start.x - 0.5) * uAspect, start.y);
          vec2 pend = vec2((end.x - 0.5) * uAspect, end.y);

          vec2 dir = normalize(pend - pstart);
          vec2 d = puv - ppos;
          float along = dot(d, dir);
          float perp = length(d - dir * along);

          float s = max(0.0, -along);
          float trailLen = 0.28;
          float tailFeather = 0.03;
          float tailWindow = 1.0 - smoothstep(trailLen, trailLen + tailFeather, s);
          float behindMask = step(along, 0.0);
          float trailMask = behindMask * tailWindow;
          trailMask *= smoothstep(0.0, 0.015, s);

          float body = exp(-perp * 1800.0) * trailMask * exp(-s * 1.5);
          float head = exp(-perp * 2800.0) * trailMask * (1.0 - smoothstep(0.02, 0.06, s));

          float fadeIn = smoothstep(0.03, 0.14, t);
          float fadeOut = 1.0 - smoothstep(0.82, 1.0, t);
          float life = fadeIn * fadeOut;

          vec3 rainA = vec3(0.90, 0.94, 1.0);
          vec3 rainB = vec3(0.50, 0.68, 0.85);
          vec3 col = mix(rainA, rainB, smoothstep(0.0, 1.0, s / trailLen));
          return col * (body * 0.85 + head * 1.15) * life;
      }

      vec3 stormRainBackdrop(vec2 uv, vec3 base, vec3 tint, float seed, float time) {
          float aspect = max(uAspect, 0.001);
          vec2 p = vec2((uv.x - 0.5) * aspect, uv.y - 0.5);

          // Darker, more dramatic storm sky with green-grey tint
          vec3 skyTop    = mix(base, vec3(0.18, 0.20, 0.25), 0.45);
          vec3 skyUpper  = mix(base, vec3(0.28, 0.32, 0.38), 0.35);
          vec3 skyMid    = mix(tint, vec3(0.38, 0.42, 0.46), 0.30);
          vec3 skyLow    = mix(tint, vec3(0.48, 0.52, 0.50), 0.25);
          vec3 skyHorizon = mix(tint, vec3(0.55, 0.56, 0.52), 0.20);

          float skyGrad = uv.y;
          vec3 sky = mix(skyHorizon, skyLow, smoothstep(0.0, 0.20, skyGrad));
          sky = mix(sky, skyMid, smoothstep(0.20, 0.40, skyGrad));
          sky = mix(sky, skyUpper, smoothstep(0.40, 0.70, skyGrad));
          sky = mix(sky, skyTop, smoothstep(0.70, 1.0, skyGrad));

          // Storm tint - slight green-grey cast
          sky = mix(sky, sky * vec3(0.90, 0.95, 0.88), 0.20);

          // Sky noise to break banding
          float skyNoise = fbm(uv * 2.0 + vec2(seed * 0.11, seed * 0.19) + time * 0.003) * 0.025;
          vec3 col = clamp(sky + skyNoise, 0.0, 1.0);

          // Atmospheric haze - heavier for storm
          float haze = exp(-length(vec2((uv.x - 0.5) * uAspect, (uv.y - 1.12))) * 1.8);
          col += vec3(0.35, 0.42, 0.50) * haze * 0.08;

          // Horizon mist layer
          float horizonMist = smoothstep(0.25, 0.0, uv.y);
          if (horizonMist > 0.0) {
              float mistNoise = fbm(vec2(uv.x * 3.0 + time * 0.008, seed * 0.7)) * 0.5 + 0.5;
              col = mix(col, vec3(0.52, 0.55, 0.54), horizonMist * mistNoise * 0.35);
          }

          // Lightning sky flash - lights up the whole sky intermittently
          float flashT = time * 1.8 + seed * 7.3;
          float flashCycle = fract(flashT);
          float skyFlash = smoothstep(0.0, 0.02, flashCycle) * smoothstep(0.06, 0.03, flashCycle);
          skyFlash += smoothstep(0.08, 0.10, flashCycle) * smoothstep(0.14, 0.11, flashCycle) * 0.5;
          float skyFlashGate = step(0.88, fract(sin(floor(flashT) * 43758.5453) * 0.5 + 0.5));
          col += vec3(0.12, 0.11, 0.16) * skyFlash * skyFlashGate * smoothstep(0.0, 0.5, uv.y);

          float drift = time * 0.012 + seed * 0.017;

          // 5 cloud banks for wall-of-storm feel (back to front)
          float cloudX1 = mod(1.2 - drift * 0.55, 2.9) - 0.95;
          vec2 cloudPos1 = vec2(cloudX1, 0.32);
          if (cloudInBounds(p, cloudPos1, 0.65, CLOUD_EXTENT_A, CLOUD_BOUNDS_PADDING)) {
              float sdf1 = animeCloudSDF(p - cloudPos1, 0.65);
              vec4 cloud1 = shadeStormCloud(sdf1, p, cloudPos1, 0.65, time, seed + 0.2);
              col = mix(col, cloud1.rgb, cloud1.a * 0.96);
          }

          float cloudX2 = mod(0.5 - drift * 0.82, 3.2) - 1.05;
          vec2 cloudPos2 = vec2(cloudX2, 0.20);
          if (cloudInBounds(p, cloudPos2, 0.58, CLOUD_EXTENT_B, CLOUD_BOUNDS_PADDING)) {
              float sdf2 = animeCloudSDF2(p - cloudPos2, 0.58);
              vec4 cloud2 = shadeStormCloud(sdf2, p, cloudPos2, 0.58, time, seed + 1.0);
              col = mix(col, cloud2.rgb, cloud2.a * 0.98);
          }

          float cloudX3 = mod(-0.3 - drift * 1.15, 2.7) - 0.85;
          vec2 cloudPos3 = vec2(cloudX3, 0.08);
          if (cloudInBounds(p, cloudPos3, 0.44, CLOUD_EXTENT_C, CLOUD_BOUNDS_PADDING)) {
              float sdf3 = animeCloudSDF3(p - cloudPos3, 0.44);
              vec4 cloud3 = shadeStormCloud(sdf3, p, cloudPos3, 0.44, time, seed + 2.0);
              col = mix(col, cloud3.rgb, cloud3.a * 1.0);
          }

          float cloudX4 = mod(2.0 - drift * 0.70, 3.5) - 1.12;
          vec2 cloudPos4 = vec2(cloudX4, 0.36);
          if (cloudInBounds(p, cloudPos4, 0.60, CLOUD_EXTENT_A, CLOUD_BOUNDS_PADDING)) {
              float sdf4 = animeCloudSDF(p - cloudPos4, 0.60);
              vec4 cloud4 = shadeStormCloud(sdf4, p, cloudPos4, 0.60, time, seed + 3.2);
              col = mix(col, cloud4.rgb, cloud4.a * 0.94);
          }

          // 5th cloud bank - low level wall of storm
          float cloudX5 = mod(-0.8 - drift * 1.38, 3.0) - 0.90;
          vec2 cloudPos5 = vec2(cloudX5, -0.04);
          if (cloudInBounds(p, cloudPos5, 0.52, CLOUD_EXTENT_B, CLOUD_BOUNDS_PADDING)) {
              float sdf5 = animeCloudSDF2(p - cloudPos5, 0.52);
              vec4 cloud5 = shadeStormCloud(sdf5, p, cloudPos5, 0.52, time, seed + 4.5);
              col = mix(col, cloud5.rgb, cloud5.a * 1.0);
          }

          // Fade rain behind clouds (top bias so rain feels like it comes from cloud mass)
          float rainFade = smoothstep(0.03, 0.18, uv.y);
          if (rainFade > 0.0) {
              // Dense rain overlay - 10 streaks for heavy downpour
              vec3 rain = vec3(0.0);
              rain += rainStreak(uv, time, seed * 11.3 + 0.10);
              rain += rainStreak(uv, time, seed * 13.7 + 0.24);
              rain += rainStreak(uv, time, seed * 17.9 + 0.41);
              rain += rainStreak(uv, time, seed * 19.1 + 0.56);
              rain += rainStreak(uv, time, seed * 23.8 + 0.72);
              rain += rainStreak(uv, time, seed * 29.4 + 0.88);
              rain += rainStreak(uv, time, seed * 7.2 + 0.15);
              rain += rainStreak(uv, time, seed * 31.6 + 0.33);
              rain += rainStreak(uv, time, seed * 37.1 + 0.62);
              rain += rainStreak(uv, time, seed * 41.3 + 0.79);
              col += rain * rainFade * 0.85;
          }

          return col;
      }

      // ============================================
      // Rainbow Sky - dreamy illustrated rainbow + cloud banks
      // Fully bundled inside the layer render path so it reveal-matches.
      // Features: 7-band primary arc, faint secondary arc, atmospheric glow,
      // sun-lit clouds via shadeAnimeCloudLit, soft god rays.
      // ============================================
      vec3 rainbowBand(float t, float saturation) {
          // 7-band spectrum: red → orange → gold → green → sky → blue → violet
          vec3 red     = vec3(0.96, 0.42, 0.38);
          vec3 coral   = vec3(0.98, 0.58, 0.42);
          vec3 peach   = vec3(0.99, 0.74, 0.46);
          vec3 gold    = vec3(0.98, 0.88, 0.50);
          vec3 mint    = vec3(0.58, 0.88, 0.66);
          vec3 sky     = vec3(0.48, 0.74, 0.92);
          vec3 lavender = vec3(0.68, 0.55, 0.86);

          float t7 = t * 6.0;
          vec3 color;
          if (t7 < 1.0)      color = mix(red, coral, t7);
          else if (t7 < 2.0) color = mix(coral, peach, t7 - 1.0);
          else if (t7 < 3.0) color = mix(peach, gold, t7 - 2.0);
          else if (t7 < 4.0) color = mix(gold, mint, t7 - 3.0);
          else if (t7 < 5.0) color = mix(mint, sky, t7 - 4.0);
          else                color = mix(sky, lavender, t7 - 5.0);

          vec3 grey = vec3(dot(color, vec3(0.299, 0.587, 0.114)));
          return mix(grey, color, saturation);
      }

      float rainbowArc(vec2 p, vec2 center, float innerR, float outerR) {
          float dist = length(p - center);
          float inner = smoothstep(innerR - 0.02, innerR + 0.01, dist);
          float outer = smoothstep(outerR + 0.02, outerR - 0.01, dist);
          float arcMask = smoothstep(-0.05, 0.05, p.y - center.y);
          return inner * outer * arcMask;
      }

      vec3 rainbowSkyBackdrop(vec2 uv, float time, float seed) {
          float aspect = max(uAspect, 0.001);
          vec2 p = vec2((uv.x - 0.5) * aspect, uv.y - 0.5);

          // Rich post-rain golden hour sky
          vec3 skyTop     = vec3(0.72, 0.62, 0.78);
          vec3 skyUpper   = vec3(0.82, 0.70, 0.78);
          vec3 skyMid     = vec3(0.93, 0.80, 0.76);
          vec3 skyLow     = vec3(0.97, 0.88, 0.78);
          vec3 skyHorizon = vec3(0.99, 0.93, 0.84);

          float skyGrad = uv.y;
          vec3 sky = mix(skyHorizon, skyLow, smoothstep(0.0, 0.18, skyGrad));
          sky = mix(sky, skyMid, smoothstep(0.18, 0.38, skyGrad));
          sky = mix(sky, skyUpper, smoothstep(0.38, 0.65, skyGrad));
          sky = mix(sky, skyTop, smoothstep(0.65, 1.0, skyGrad));

          // Sky noise to break banding
          float skyNoise = fbm(uv * 1.5 + vec2(seed * 0.13, seed * 0.21) + time * 0.002) * 0.025;
          vec3 col = clamp(sky + skyNoise, 0.0, 1.0);

          // Warm horizon glow (post-rain sunlight)
          float horizonGlow = smoothstep(0.30, 0.0, uv.y);
          col = mix(col, vec3(0.99, 0.92, 0.78), horizonGlow * 0.15);

          // Soft god rays from behind rainbow center
          vec2 sunPos = vec2(0.0, 0.18);
          float sunDist = length(p - sunPos);
          float sunRayGate = smoothstep(0.0, 0.15, sunDist) * smoothstep(0.8, 0.2, sunDist);
          if (sunRayGate > 0.0) {
              vec2 sunDir = p - sunPos;
              vec2 sunDirNorm = sunDir / max(length(sunDir), 0.0001);
              for (int i = 0; i < 5; i++) {
                  float fi = float(i);
                  float rayAngle = 0.4 + fi * 0.55 + sin(fi * 2.3 + seed) * 0.15;
                  vec2 rayDir = normalize(vec2(cos(rayAngle), sin(rayAngle)));
                  float rayDot = dot(sunDirNorm, rayDir);
                  float ray = pow(max(rayDot, 0.0), 28.0 + fi * 8.0);
                  col += vec3(0.99, 0.94, 0.82) * ray * sunRayGate * 0.025;
              }
          }

          // ---- Primary rainbow arc ----
          vec2 rainbowCenter = vec2(0.0, -0.42);
          float innerRadius = 0.50;
          float outerRadius = 0.74;

          float dist = length(p - rainbowCenter);
          float t = clamp((dist - innerRadius) / (outerRadius - innerRadius), 0.0, 1.0);
          float angleFade = smoothstep(-0.06, 0.12, p.y - rainbowCenter.y);

          float rimW = 0.038;
          float inset = 0.017;

          float arcFill = rainbowArc(p, rainbowCenter, innerRadius, outerRadius);
          float arcCore = rainbowArc(p, rainbowCenter, innerRadius + inset, outerRadius - inset);

          float rimOuter = rainbowArc(p, rainbowCenter, outerRadius, outerRadius + rimW);
          float rimInner = rainbowArc(p, rainbowCenter, innerRadius - rimW, innerRadius);
          float rimMask = clamp(rimOuter + rimInner, 0.0, 1.0);

          float shadow = rainbowArc(
              p + vec2(0.026, -0.028),
              rainbowCenter,
              innerRadius - rimW * 0.35,
              outerRadius + rimW * 0.85
          );

          float rimGrain = vnoise(p * 22.0 + vec2(11.7, 3.4) + seed * 0.07);
          float rimAlpha = rimMask * angleFade * (0.42 + rimGrain * 0.18);
          vec3 rimCol = vec3(0.99, 0.96, 0.93);

          col = mix(col, col * vec3(0.80, 0.74, 0.78), shadow * angleFade * 0.22);
          col = mix(col, rimCol, rimAlpha);

          // Atmospheric glow around primary arc
          float glowInner = smoothstep(innerRadius - 0.12, innerRadius, dist);
          float glowOuter = smoothstep(outerRadius + 0.12, outerRadius, dist);
          float arcGlow = glowInner * glowOuter * angleFade;
          col = mix(col, vec3(0.99, 0.95, 0.92), arcGlow * 0.08);

          if (arcCore > 0.0) {
              vec3 rainbowCol = rainbowBand(t, 0.88);

              float belly = smoothstep(0.05, 0.45, t) * smoothstep(0.95, 0.58, t);
              rainbowCol = mix(rainbowCol, rainbowCol * 0.90, clamp(arcFill - arcCore, 0.0, 1.0));
              rainbowCol = mix(rainbowCol, rainbowCol + vec3(0.06, 0.05, 0.04), belly * 0.55);

              float edgeFade = smoothstep(0.0, 0.16, t) * smoothstep(1.0, 0.84, t);
              float rainbowAlpha = arcCore * edgeFade * angleFade * 0.75;
              col = mix(col, rainbowCol, rainbowAlpha);

              float highlight = rainbowArc(p, rainbowCenter, outerRadius - 0.05, outerRadius - 0.016);
              float highlightGrain = vnoise(p * 18.0 + vec2(6.1, 9.3) + seed * 0.09);
              col = mix(col, rimCol, highlight * angleFade * (0.12 + highlightGrain * 0.08));
          }

          // ---- Secondary (double) rainbow arc ----
          // Fainter, wider, colors reversed (violet outside, red inside)
          float secInner = outerRadius + 0.10;
          float secOuter = outerRadius + 0.30;
          float secDist = dist;
          float secT = clamp((secDist - secInner) / (secOuter - secInner), 0.0, 1.0);
          float secAngleFade = smoothstep(-0.04, 0.14, p.y - rainbowCenter.y);

          float secArc = rainbowArc(p, rainbowCenter, secInner + 0.01, secOuter - 0.01);
          if (secArc > 0.0) {
              // Reversed band (1.0 - t for color reversal)
              vec3 secCol = rainbowBand(1.0 - secT, 0.60);
              float secEdgeFade = smoothstep(0.0, 0.20, secT) * smoothstep(1.0, 0.80, secT);
              float secAlpha = secArc * secEdgeFade * secAngleFade * 0.28;
              col = mix(col, secCol, secAlpha);
          }

          // Alexander's dark band (slightly darker between primary and secondary)
          float darkBandInner = outerRadius + 0.01;
          float darkBandOuter = secInner - 0.01;
          float darkDist2 = dist;
          float darkT = smoothstep(darkBandInner, darkBandInner + 0.03, darkDist2)
                       * smoothstep(darkBandOuter, darkBandOuter - 0.03, darkDist2);
          col = mix(col, col * 0.94, darkT * angleFade * 0.35);

          // ---- Sun-lit cloud banks under rainbow feet ----
          float rainbowFootY = rainbowCenter.y;
          float rainbowFootX = (innerRadius + outerRadius) * 0.5 * 0.95;

          vec2 leftCloudPos = vec2(-rainbowFootX - 0.15, rainbowFootY - 0.18);
          if (cloudInBounds(p, leftCloudPos, 0.62, CLOUD_EXTENT_A, CLOUD_BOUNDS_PADDING)) {
              float sdfLeft = animeCloudSDF(p - leftCloudPos, 0.62);
              vec4 cloudLeft = shadeAnimeCloudLit(sdfLeft, p, leftCloudPos, 0.62, 2.45, sunPos);
              col = mix(col, cloudLeft.rgb, cloudLeft.a);
          }

          vec2 rightCloudPos = vec2(rainbowFootX + 0.15, rainbowFootY - 0.18);
          if (cloudInBounds(p, rightCloudPos, 0.62, CLOUD_EXTENT_A, CLOUD_BOUNDS_PADDING)) {
              float sdfRight = animeCloudSDF(p - rightCloudPos, 0.62);
              vec4 cloudRight = shadeAnimeCloudLit(sdfRight, p, rightCloudPos, 0.62, 2.45, sunPos);
              col = mix(col, cloudRight.rgb, cloudRight.a);
          }

          vec2 puffL = vec2(-rainbowFootX + 0.08, rainbowFootY - 0.08);
          if (cloudInBounds(p, puffL, 0.30, CLOUD_EXTENT_C, CLOUD_BOUNDS_PADDING)) {
              float sdfPL = animeCloudSDF3(p - puffL, 0.30);
              vec4 cloudPL = shadeAnimeCloudLit(sdfPL, p, puffL, 0.30, 2.55, sunPos);
              col = mix(col, cloudPL.rgb, cloudPL.a * 0.9);
          }

          vec2 puffR = vec2(rainbowFootX - 0.08, rainbowFootY - 0.08);
          if (cloudInBounds(p, puffR, 0.30, CLOUD_EXTENT_C, CLOUD_BOUNDS_PADDING)) {
              float sdfPR = animeCloudSDF3(p - puffR, 0.30);
              vec4 cloudPR = shadeAnimeCloudLit(sdfPR, p, puffR, 0.30, 2.55, sunPos);
              col = mix(col, cloudPR.rgb, cloudPR.a * 0.9);
          }

          // Additional wispy puffs for depth (mid-sky)
          vec2 wispsL = vec2(-0.35, 0.12);
          if (cloudInBounds(p, wispsL, 0.22, CLOUD_EXTENT_B, CLOUD_BOUNDS_PADDING)) {
              float sdfWL = animeCloudSDF2(p - wispsL, 0.22);
              vec4 cloudWL = shadeAnimeCloudLit(sdfWL, p, wispsL, 0.22, 2.50, sunPos);
              col = mix(col, cloudWL.rgb, cloudWL.a * 0.65);
          }

          vec2 wispsR = vec2(0.42, 0.16);
          if (cloudInBounds(p, wispsR, 0.18, CLOUD_EXTENT_C, CLOUD_BOUNDS_PADDING)) {
              float sdfWR = animeCloudSDF3(p - wispsR, 0.18);
              vec4 cloudWR = shadeAnimeCloudLit(sdfWR, p, wispsR, 0.18, 2.48, sunPos);
              col = mix(col, cloudWR.rgb, cloudWR.a * 0.55);
          }

          return clamp(col, 0.0, 1.0);
      }

      vec3 renderLayer(vec2 uv, vec3 base, vec3 tint, float seed, float kind, float enabled, vec3 textColor) {
          vec3 col;
          if (kind < 0.5) {
              col = surfaceLayer(uv, base, tint, seed);
          } else if (kind < 1.5) {
              col = starryBackdrop(uv, base, tint, seed, uTime);
          } else if (kind < 2.5) {
              col = sunriseBackdrop(uv, uTime);
          } else if (kind < 3.5) {
              col = snowyBackdrop(uv, base, tint, seed, uTime);
          } else if (kind < 4.5) {
              col = betaSkyBackdrop(uv, base, tint, uTime, seed);
          } else if (kind < 5.5) {
              col = stormRainBackdrop(uv, base, tint, seed, uTime);
          } else {
              col = rainbowSkyBackdrop(uv, uTime, seed);
          }
          col = applyText(col, uv, enabled, textColor);
          return col;
      }

      void main () {
          vec3 c = texture2D(uTexture, vUv).rgb;
	          float mask = maskFromColor(c);

          vec3 lc = texture2D(uTexture, vL).rgb;
          vec3 rc = texture2D(uTexture, vR).rgb;
          vec3 tc = texture2D(uTexture, vT).rgb;
          vec3 bc = texture2D(uTexture, vB).rgb;

          float lMask = maskFromColor(lc);
          float rMask = maskFromColor(rc);
          float tMask = maskFromColor(tc);
          float bMask = maskFromColor(bc);

          vec2 flow = vec2(rMask - lMask, tMask - bMask);

          #ifdef SHADING
              float dx = length(rc) - length(lc);
              float dy = length(tc) - length(bc);

              vec3 n = normalize(vec3(dx, dy, length(texelSize)));
              vec3 l = vec3(0.0, 0.0, 1.0);

              float diffuse = clamp(dot(n, l) + 0.85, 0.85, 1.0);
              mask *= diffuse;
          #endif

	          // Suppress tiny “speck” reveals that can show up as a single pixel dot near edges.
	          mask = smoothstep(0.10, 0.5, mask);
	          mask = pow(mask, 0.7);

          vec2 transitionDelta = vUv - uTransitionCenter;
          transitionDelta.x *= uAspect;
          float transitionDist = length(transitionDelta);
          float transitionMask =
              (1.0 - smoothstep(uTransitionRadius, uTransitionRadius + 0.02, transitionDist)) *
              uTransitionActive;

          float combinedMask = max(mask, transitionMask);

          float revealMix = smoothstep(0.02, 0.12, combinedMask);

          float alpha = 1.0;
          vec3 color = vec3(0.0);
          if (uAlphaMode > 2.5) {
	              vec3 revealBg = renderLayer(vUv, uBackBase, uBackTint, uBackSeed, uBackKind, 0.0, uBackTextColor);
	              float textEnabled = mix(uFrontTextEnabled, uBackTextEnabled, revealMix);
	              vec3 textColor = mix(uFrontTextColor, uBackTextColor, revealMix);
	              float aText = overlayMask(vUv, textEnabled);

	              alpha = aText + revealMix * (1.0 - aText);
	              color = textColor * aText + revealBg * revealMix * (1.0 - aText);
          } else if (uAlphaMode > 1.5) {
              vec3 front = renderLayer(vUv, uFrontBase, uFrontTint, uFrontSeed, uFrontKind, uFrontTextEnabled, uFrontTextColor);
              float stainMask = smoothstep(0.0, 0.2, combinedMask);
              alpha = stainMask;
              color = front * alpha;
          } else if (uAlphaMode > 0.5) {
              vec3 front = renderLayer(vUv, uFrontBase, uFrontTint, uFrontSeed, uFrontKind, uFrontTextEnabled, uFrontTextColor);
              float revealMask = step(0.01, combinedMask * 1.8);
              alpha = 1.0 - revealMask;
              color = front * alpha;
          } else if (revealMix <= 0.0) {
              color = renderLayer(vUv, uFrontBase, uFrontTint, uFrontSeed, uFrontKind, uFrontTextEnabled, uFrontTextColor);
          } else if (revealMix >= 1.0) {
              color = renderLayer(vUv, uBackBase, uBackTint, uBackSeed, uBackKind, uBackTextEnabled, uBackTextColor);
          } else {
              vec3 front = renderLayer(vUv, uFrontBase, uFrontTint, uFrontSeed, uFrontKind, uFrontTextEnabled, uFrontTextColor);
              vec3 back = renderLayer(vUv, uBackBase, uBackTint, uBackSeed, uBackKind, uBackTextEnabled, uBackTextColor);
              color = mix(front, back, revealMix);
          }
          gl_FragColor = vec4(color, alpha);
      }
    `;

    const splatShader = compileShader(
      gl.FRAGMENT_SHADER,
      `
        precision highp float;
        precision highp sampler2D;
        varying vec2 vUv;
        uniform sampler2D uTarget;
        uniform float aspectRatio;
        uniform vec3 color;
        uniform vec2 point;
        uniform float radius;

        void main () {
            vec2 p = vUv - point.xy;
            p.x *= aspectRatio;
            vec3 splat = exp(-dot(p, p) / radius) * color;
            vec3 base = texture2D(uTarget, vUv).xyz;
            gl_FragColor = vec4(base + splat, 1.0);
        }
      `,
    );

    const advectionShader = compileShader(
      gl.FRAGMENT_SHADER,
      `
        precision highp float;
        precision highp sampler2D;
        varying vec2 vUv;
        uniform sampler2D uVelocity;
        uniform sampler2D uSource;
        uniform vec2 texelSize;
        uniform vec2 dyeTexelSize;
        uniform float dt;
        uniform float dissipation;

        vec4 bilerp (sampler2D sam, vec2 uv, vec2 tsize) {
            vec2 st = uv / tsize - 0.5;
            vec2 iuv = floor(st);
            vec2 fuv = fract(st);

            vec4 a = texture2D(sam, (iuv + vec2(0.5, 0.5)) * tsize);
            vec4 b = texture2D(sam, (iuv + vec2(1.5, 0.5)) * tsize);
            vec4 c = texture2D(sam, (iuv + vec2(0.5, 1.5)) * tsize);
            vec4 d = texture2D(sam, (iuv + vec2(1.5, 1.5)) * tsize);

            return mix(mix(a, b, fuv.x), mix(c, d, fuv.x), fuv.y);
        }

        void main () {
            #ifdef MANUAL_FILTERING
                vec2 coord = vUv - dt * bilerp(uVelocity, vUv, texelSize).xy * texelSize;
                vec4 result = bilerp(uSource, coord, dyeTexelSize);
            #else
                vec2 coord = vUv - dt * texture2D(uVelocity, vUv).xy * texelSize;
                vec4 result = texture2D(uSource, coord);
            #endif
            float decay = 1.0 + dissipation * dt;
            gl_FragColor = result / decay;
        }
      `,
      ext.supportLinearFiltering ? null : ["MANUAL_FILTERING"],
    );

    const divergenceShader = compileShader(
      gl.FRAGMENT_SHADER,
      `
        precision mediump float;
        precision mediump sampler2D;
        varying highp vec2 vUv;
        varying highp vec2 vL;
        varying highp vec2 vR;
        varying highp vec2 vT;
        varying highp vec2 vB;
        uniform sampler2D uVelocity;

        void main () {
            float L = texture2D(uVelocity, vL).x;
            float R = texture2D(uVelocity, vR).x;
            float T = texture2D(uVelocity, vT).y;
            float B = texture2D(uVelocity, vB).y;

            vec2 C = texture2D(uVelocity, vUv).xy;
            if (vL.x < 0.0) { L = -C.x; }
            if (vR.x > 1.0) { R = -C.x; }
            if (vT.y > 1.0) { T = -C.y; }
            if (vB.y < 0.0) { B = -C.y; }

            float div = 0.5 * (R - L + T - B);
            gl_FragColor = vec4(div, 0.0, 0.0, 1.0);
        }
      `,
    );

    const curlShader = compileShader(
      gl.FRAGMENT_SHADER,
      `
        precision mediump float;
        precision mediump sampler2D;
        varying highp vec2 vUv;
        varying highp vec2 vL;
        varying highp vec2 vR;
        varying highp vec2 vT;
        varying highp vec2 vB;
        uniform sampler2D uVelocity;

        void main () {
            float L = texture2D(uVelocity, vL).y;
            float R = texture2D(uVelocity, vR).y;
            float T = texture2D(uVelocity, vT).x;
            float B = texture2D(uVelocity, vB).x;
            float vorticity = R - L - T + B;
            gl_FragColor = vec4(0.5 * vorticity, 0.0, 0.0, 1.0);
        }
      `,
    );

    const vorticityShader = compileShader(
      gl.FRAGMENT_SHADER,
      `
        precision highp float;
        precision highp sampler2D;
        varying vec2 vUv;
        varying vec2 vL;
        varying vec2 vR;
        varying vec2 vT;
        varying vec2 vB;
        uniform sampler2D uVelocity;
        uniform sampler2D uCurl;
        uniform float curl;
        uniform float dt;

        void main () {
            float L = texture2D(uCurl, vL).x;
            float R = texture2D(uCurl, vR).x;
            float T = texture2D(uCurl, vT).x;
            float B = texture2D(uCurl, vB).x;
            float C = texture2D(uCurl, vUv).x;

            vec2 force = 0.5 * vec2(abs(T) - abs(B), abs(R) - abs(L));
            force /= length(force) + 0.0001;
            force *= curl * C;
            force.y *= -1.0;

            vec2 velocity = texture2D(uVelocity, vUv).xy;
            velocity += force * dt;
            velocity = min(max(velocity, -1000.0), 1000.0);
            gl_FragColor = vec4(velocity, 0.0, 1.0);
        }
      `,
    );

    const pressureShader = compileShader(
      gl.FRAGMENT_SHADER,
      `
        precision mediump float;
        precision mediump sampler2D;
        varying highp vec2 vUv;
        varying highp vec2 vL;
        varying highp vec2 vR;
        varying highp vec2 vT;
        varying highp vec2 vB;
        uniform sampler2D uPressure;
        uniform sampler2D uDivergence;

        void main () {
            float L = texture2D(uPressure, vL).x;
            float R = texture2D(uPressure, vR).x;
            float T = texture2D(uPressure, vT).x;
            float B = texture2D(uPressure, vB).x;
            float C = texture2D(uPressure, vUv).x;
            float divergence = texture2D(uDivergence, vUv).x;
            float pressure = (L + R + B + T - divergence) * 0.25;
            gl_FragColor = vec4(pressure, 0.0, 0.0, 1.0);
        }
      `,
    );

    const gradientSubtractShader = compileShader(
      gl.FRAGMENT_SHADER,
      `
        precision mediump float;
        precision mediump sampler2D;
        varying highp vec2 vUv;
        varying highp vec2 vL;
        varying highp vec2 vR;
        varying highp vec2 vT;
        varying highp vec2 vB;
        uniform sampler2D uPressure;
        uniform sampler2D uVelocity;

        void main () {
            float L = texture2D(uPressure, vL).x;
            float R = texture2D(uPressure, vR).x;
            float T = texture2D(uPressure, vT).x;
            float B = texture2D(uPressure, vB).x;
            vec2 velocity = texture2D(uVelocity, vUv).xy;
            velocity.xy -= vec2(R - L, T - B);
            gl_FragColor = vec4(velocity, 0.0, 1.0);
        }
      `,
    );

    const blit = (() => {
      gl.bindBuffer(gl.ARRAY_BUFFER, gl.createBuffer());
      gl.bufferData(
        gl.ARRAY_BUFFER,
        new Float32Array([-1, -1, -1, 1, 1, 1, 1, -1]),
        gl.STATIC_DRAW,
      );
      gl.bindBuffer(gl.ELEMENT_ARRAY_BUFFER, gl.createBuffer());
      gl.bufferData(
        gl.ELEMENT_ARRAY_BUFFER,
        new Uint16Array([0, 1, 2, 0, 2, 3]),
        gl.STATIC_DRAW,
      );
      gl.vertexAttribPointer(0, 2, gl.FLOAT, false, 0, 0);
      gl.enableVertexAttribArray(0);
      return (target, clear = false) => {
        if (target == null) {
          gl.viewport(0, 0, gl.drawingBufferWidth, gl.drawingBufferHeight);
          gl.bindFramebuffer(gl.FRAMEBUFFER, null);
        } else {
          gl.viewport(0, 0, target.width, target.height);
          gl.bindFramebuffer(gl.FRAMEBUFFER, target.fbo);
        }
        if (clear) {
          gl.clearColor(0.0, 0.0, 0.0, 1.0);
          gl.clear(gl.COLOR_BUFFER_BIT);
        }
        gl.drawElements(gl.TRIANGLES, 6, gl.UNSIGNED_SHORT, 0);
      };
    })();

    let dye, velocity, divergence, curl, pressure;

    const copyProgram = new Program(baseVertexShader, copyShader);
    const clearProgram = new Program(baseVertexShader, clearShader);
    const splatProgram = new Program(baseVertexShader, splatShader);
    const advectionProgram = new Program(baseVertexShader, advectionShader);
    const divergenceProgram = new Program(baseVertexShader, divergenceShader);
    const curlProgram = new Program(baseVertexShader, curlShader);
    const vorticityProgram = new Program(baseVertexShader, vorticityShader);
    const pressureProgram = new Program(baseVertexShader, pressureShader);
    const gradienSubtractProgram = new Program(
      baseVertexShader,
      gradientSubtractShader,
    );
    const displayMaterial = new Material(baseVertexShader, displayShaderSource);

    function initFramebuffers() {
      let simRes = getResolution(config.SIM_RESOLUTION);
      let dyeRes = getResolution(config.DYE_RESOLUTION);
      const texType = ext.halfFloatTexType;
      const rgba = ext.formatRGBA;
      const rg = ext.formatRG;
      const r = ext.formatR;
      const filtering = ext.supportLinearFiltering ? gl.LINEAR : gl.NEAREST;
      gl.disable(gl.BLEND);

      if (!dye)
        dye = createDoubleFBO(
          dyeRes.width,
          dyeRes.height,
          rgba.internalFormat,
          rgba.format,
          texType,
          filtering,
        );
      else
        dye = resizeDoubleFBO(
          dye,
          dyeRes.width,
          dyeRes.height,
          rgba.internalFormat,
          rgba.format,
          texType,
          filtering,
        );

      if (!velocity)
        velocity = createDoubleFBO(
          simRes.width,
          simRes.height,
          rg.internalFormat,
          rg.format,
          texType,
          filtering,
        );
      else
        velocity = resizeDoubleFBO(
          velocity,
          simRes.width,
          simRes.height,
          rg.internalFormat,
          rg.format,
          texType,
          filtering,
        );

      divergence = createFBO(
        simRes.width,
        simRes.height,
        r.internalFormat,
        r.format,
        texType,
        gl.NEAREST,
      );
      curl = createFBO(
        simRes.width,
        simRes.height,
        r.internalFormat,
        r.format,
        texType,
        gl.NEAREST,
      );
      pressure = createDoubleFBO(
        simRes.width,
        simRes.height,
        r.internalFormat,
        r.format,
        texType,
        gl.NEAREST,
      );
    }

    function createFBO(w, h, internalFormat, format, type, param) {
      gl.activeTexture(gl.TEXTURE0);
      let texture = gl.createTexture();
      gl.bindTexture(gl.TEXTURE_2D, texture);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, param);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, param);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
      gl.texImage2D(
        gl.TEXTURE_2D,
        0,
        internalFormat,
        w,
        h,
        0,
        format,
        type,
        null,
      );

      let fbo = gl.createFramebuffer();
      gl.bindFramebuffer(gl.FRAMEBUFFER, fbo);
      gl.framebufferTexture2D(
        gl.FRAMEBUFFER,
        gl.COLOR_ATTACHMENT0,
        gl.TEXTURE_2D,
        texture,
        0,
      );
      gl.viewport(0, 0, w, h);
      gl.clear(gl.COLOR_BUFFER_BIT);

      let texelSizeX = 1.0 / w;
      let texelSizeY = 1.0 / h;
      return {
        texture,
        fbo,
        width: w,
        height: h,
        texelSizeX,
        texelSizeY,
        attach(id) {
          gl.activeTexture(gl.TEXTURE0 + id);
          gl.bindTexture(gl.TEXTURE_2D, texture);
          return id;
        },
      };
    }

    function createDoubleFBO(w, h, internalFormat, format, type, param) {
      let fbo1 = createFBO(w, h, internalFormat, format, type, param);
      let fbo2 = createFBO(w, h, internalFormat, format, type, param);
      return {
        width: w,
        height: h,
        texelSizeX: fbo1.texelSizeX,
        texelSizeY: fbo1.texelSizeY,
        get read() {
          return fbo1;
        },
        set read(value) {
          fbo1 = value;
        },
        get write() {
          return fbo2;
        },
        set write(value) {
          fbo2 = value;
        },
        swap() {
          let temp = fbo1;
          fbo1 = fbo2;
          fbo2 = temp;
        },
      };
    }

    function resizeFBO(target, w, h, internalFormat, format, type, param) {
      let newFBO = createFBO(w, h, internalFormat, format, type, param);
      copyProgram.bind();
      gl.uniform1i(copyProgram.uniforms.uTexture, target.attach(0));
      blit(newFBO);
      return newFBO;
    }

    function resizeDoubleFBO(
      target,
      w,
      h,
      internalFormat,
      format,
      type,
      param,
    ) {
      if (target.width === w && target.height === h) return target;
      target.read = resizeFBO(
        target.read,
        w,
        h,
        internalFormat,
        format,
        type,
        param,
      );
      target.write = createFBO(w, h, internalFormat, format, type, param);
      target.width = w;
      target.height = h;
      target.texelSizeX = 1.0 / w;
      target.texelSizeY = 1.0 / h;
      return target;
    }

    function updateKeywords() {
      let displayKeywords = [];
      if (config.SHADING) displayKeywords.push("SHADING");
      displayMaterial.setKeywords(displayKeywords);
    }

    updateKeywords();
    initFramebuffers();
    let lastUpdateTime = performance.now();
    let colorUpdateTimer = 0.0;
    let isDocumentHidden = document.hidden;
    let pixelRatio = Math.min(
      window.devicePixelRatio || 1,
      MAX_DEVICE_PIXEL_RATIO,
    );
    let needsResize = true;
    let displayProgram = null;
    let cachedPalette = null;
    let cachedSurface = null;
    let cachedAlphaMode = Number.NaN;
    let cachedResolutionWidth = -1;
    let cachedResolutionHeight = -1;
    let cachedTexelWidth = -1;
    let cachedTexelHeight = -1;
    let cachedNameMarkReady = Number.NaN;
    let cachedNameMarkAspect = Number.NaN;
    let cachedSmashReady = Number.NaN;
    let cachedSmashAspect = Number.NaN;
    let cachedSmashTexelX = Number.NaN;
    let cachedSmashTexelY = Number.NaN;
    let cachedSmashFrameCount = Number.NaN;
    let cachedSmashFrameColumns = Number.NaN;
    let cachedSmashFrameRows = Number.NaN;
    let cachedSmashFps = Number.NaN;
    let cachedTransitionCenterX = Number.NaN;
    let cachedTransitionCenterY = Number.NaN;
    let cachedTransitionRadius = Number.NaN;
    let cachedTransitionActive = Number.NaN;
    let cachedAspect = Number.NaN;
    let nameMarkSamplerSet = false;
    let smashSamplerSet = false;
    let resizeObserver = null;

    function invalidateDisplayUniformCache() {
      displayProgram = null;
      cachedPalette = null;
      cachedSurface = null;
      cachedAlphaMode = Number.NaN;
      cachedResolutionWidth = -1;
      cachedResolutionHeight = -1;
      cachedTexelWidth = -1;
      cachedTexelHeight = -1;
      cachedNameMarkReady = Number.NaN;
      cachedNameMarkAspect = Number.NaN;
      cachedSmashReady = Number.NaN;
      cachedSmashAspect = Number.NaN;
      cachedSmashTexelX = Number.NaN;
      cachedSmashTexelY = Number.NaN;
      cachedSmashFrameCount = Number.NaN;
      cachedSmashFrameColumns = Number.NaN;
      cachedSmashFrameRows = Number.NaN;
      cachedSmashFps = Number.NaN;
      cachedTransitionCenterX = Number.NaN;
      cachedTransitionCenterY = Number.NaN;
      cachedTransitionRadius = Number.NaN;
      cachedTransitionActive = Number.NaN;
      cachedAspect = Number.NaN;
      nameMarkSamplerSet = false;
      smashSamplerSet = false;
    }

    if (typeof ResizeObserver !== "undefined") {
      resizeObserver = new ResizeObserver(() => {
        needsResize = true;
      });
      resizeObserver.observe(canvas);
    }

    function updateFrame(now = performance.now()) {
      if (!isActive) return;
      if (isDocumentHidden) {
        lastUpdateTime = now;
        animationFrameId.current = null;
        return;
      }
      const dt = calcDeltaTime(now);
      if (resizeCanvas()) initFramebuffers();
      updateColors(dt);
      applyInputs();
      step(dt);
      render(null, now);
      animationFrameId.current = requestAnimationFrame(updateFrame);
    }

    function clearDoubleFbo(target) {
      if (!target) return;
      clearProgram.bind();
      gl.uniform1f(clearProgram.uniforms.value, 0.0);
      gl.uniform1i(clearProgram.uniforms.uTexture, target.read.attach(0));
      blit(target.write);
      target.swap();
      gl.uniform1i(clearProgram.uniforms.uTexture, target.read.attach(0));
      blit(target.write);
      target.swap();
    }

    // Number of frames after a reset during which applyInputs() is
    // suppressed.  This prevents the very first pointer splat from
    // appearing before the cleared FBOs have been rendered at least once.
    let resetCooldown = 0;

    function resetFluid() {
      clearDoubleFbo(dye);
      clearDoubleFbo(velocity);
      clearDoubleFbo(pressure);

      // Also hard-clear both sides of each double-FBO at the GL level
      // to guarantee no sub-pixel residue survives the shader clear.
      [dye, velocity, pressure].forEach((fbo) => {
        if (!fbo) return;
        gl.bindFramebuffer(gl.FRAMEBUFFER, fbo.read.fbo);
        gl.clear(gl.COLOR_BUFFER_BIT);
        gl.bindFramebuffer(gl.FRAMEBUFFER, fbo.write.fbo);
        gl.clear(gl.COLOR_BUFFER_BIT);
      });
      // Zero out single FBOs used by the sim
      [curl, divergence].forEach((fbo) => {
        if (!fbo) return;
        gl.bindFramebuffer(gl.FRAMEBUFFER, fbo.fbo);
        gl.clear(gl.COLOR_BUFFER_BIT);
      });
      gl.bindFramebuffer(gl.FRAMEBUFFER, null);

      clearPointerMotion();

      // Suppress pointer splats for a few frames so no stale mouse
      // movement re-introduces dye before the first clean render.
      resetCooldown = 3;

      // Also kill any lingering transition so stale reveal circles
      // don't bleed old-layer dye into the new layer.
      transitionRef.current.active = false;
      transitionRef.current.hold = false;
    }

    resetRef.current = resetFluid;

    function calcDeltaTime(now) {
      let dt = (now - lastUpdateTime) / 1000;
      dt = Math.min(dt, 0.016666);
      lastUpdateTime = now;
      return dt;
    }

    function resizeCanvas() {
      const nextPixelRatio = Math.min(
        window.devicePixelRatio || 1,
        MAX_DEVICE_PIXEL_RATIO,
      );
      if (nextPixelRatio !== pixelRatio) {
        pixelRatio = nextPixelRatio;
        needsResize = true;
      }
      if (!needsResize) return false;

      let width = scaleByPixelRatio(canvas.clientWidth);
      let height = scaleByPixelRatio(canvas.clientHeight);
      if (canvas.width !== width || canvas.height !== height) {
        canvas.width = width;
        canvas.height = height;
        needsResize = false;
        invalidateDisplayUniformCache();
        return true;
      }
      needsResize = false;
      return false;
    }

    function updateColors(dt) {
      if (config.COLOR_UPDATE_SPEED <= 0) return;
      colorUpdateTimer += dt * config.COLOR_UPDATE_SPEED;
      if (colorUpdateTimer >= 1) {
        colorUpdateTimer = wrap(colorUpdateTimer, 0, 1);
        pointers.forEach((p) => {
          p.color = generateColor();
        });
      }
    }

    function clearPointerMotion() {
      pointers.forEach((pointer) => {
        pointer.prevTexcoordX = pointer.texcoordX;
        pointer.prevTexcoordY = pointer.texcoordY;
        pointer.deltaX = 0;
        pointer.deltaY = 0;
        pointer.moved = false;
      });
    }

    function applyInputs() {
      const transition = transitionRef.current;
      if (transition.active || transition.hold) {
        clearPointerMotion();
        return;
      }
      // After a reset, suppress splats for a few frames so stale
      // pointer movement doesn't re-introduce dye immediately.
      if (resetCooldown > 0) {
        resetCooldown--;
        clearPointerMotion();
        return;
      }
      pointers.forEach((p) => {
        if (p.moved) {
          p.moved = false;
          splatPointer(p);
        }
      });
    }

    function step(dt) {
      gl.disable(gl.BLEND);
      curlProgram.bind();
      gl.uniform2f(
        curlProgram.uniforms.texelSize,
        velocity.texelSizeX,
        velocity.texelSizeY,
      );
      gl.uniform1i(curlProgram.uniforms.uVelocity, velocity.read.attach(0));
      blit(curl);

      if (config.CURL > 0) {
        vorticityProgram.bind();
        gl.uniform2f(
          vorticityProgram.uniforms.texelSize,
          velocity.texelSizeX,
          velocity.texelSizeY,
        );
        gl.uniform1i(
          vorticityProgram.uniforms.uVelocity,
          velocity.read.attach(0),
        );
        gl.uniform1i(vorticityProgram.uniforms.uCurl, curl.attach(1));
        gl.uniform1f(vorticityProgram.uniforms.curl, config.CURL);
        gl.uniform1f(vorticityProgram.uniforms.dt, dt);
        blit(velocity.write);
        velocity.swap();
      }

      divergenceProgram.bind();
      gl.uniform2f(
        divergenceProgram.uniforms.texelSize,
        velocity.texelSizeX,
        velocity.texelSizeY,
      );
      gl.uniform1i(
        divergenceProgram.uniforms.uVelocity,
        velocity.read.attach(0),
      );
      blit(divergence);

      clearProgram.bind();
      gl.uniform1i(clearProgram.uniforms.uTexture, pressure.read.attach(0));
      gl.uniform1f(clearProgram.uniforms.value, config.PRESSURE);
      blit(pressure.write);
      pressure.swap();

      pressureProgram.bind();
      gl.uniform2f(
        pressureProgram.uniforms.texelSize,
        velocity.texelSizeX,
        velocity.texelSizeY,
      );
      gl.uniform1i(pressureProgram.uniforms.uDivergence, divergence.attach(0));
      for (let i = 0; i < config.PRESSURE_ITERATIONS; i++) {
        gl.uniform1i(
          pressureProgram.uniforms.uPressure,
          pressure.read.attach(1),
        );
        blit(pressure.write);
        pressure.swap();
      }

      gradienSubtractProgram.bind();
      gl.uniform2f(
        gradienSubtractProgram.uniforms.texelSize,
        velocity.texelSizeX,
        velocity.texelSizeY,
      );
      gl.uniform1i(
        gradienSubtractProgram.uniforms.uPressure,
        pressure.read.attach(0),
      );
      gl.uniform1i(
        gradienSubtractProgram.uniforms.uVelocity,
        velocity.read.attach(1),
      );
      blit(velocity.write);
      velocity.swap();

      advectionProgram.bind();
      gl.uniform2f(
        advectionProgram.uniforms.texelSize,
        velocity.texelSizeX,
        velocity.texelSizeY,
      );
      if (!ext.supportLinearFiltering)
        gl.uniform2f(
          advectionProgram.uniforms.dyeTexelSize,
          velocity.texelSizeX,
          velocity.texelSizeY,
        );
      let velocityId = velocity.read.attach(0);
      gl.uniform1i(advectionProgram.uniforms.uVelocity, velocityId);
      gl.uniform1i(advectionProgram.uniforms.uSource, velocityId);
      gl.uniform1f(advectionProgram.uniforms.dt, dt);
      gl.uniform1f(
        advectionProgram.uniforms.dissipation,
        config.VELOCITY_DISSIPATION,
      );
      blit(velocity.write);
      velocity.swap();

      if (!ext.supportLinearFiltering)
        gl.uniform2f(
          advectionProgram.uniforms.dyeTexelSize,
          dye.texelSizeX,
          dye.texelSizeY,
        );
      gl.uniform1i(
        advectionProgram.uniforms.uVelocity,
        velocity.read.attach(0),
      );
      gl.uniform1i(advectionProgram.uniforms.uSource, dye.read.attach(1));
      gl.uniform1f(
        advectionProgram.uniforms.dissipation,
        config.DENSITY_DISSIPATION,
      );
      blit(dye.write);
      dye.swap();
    }

    function render(target, now) {
      if (alphaModeRef.current > 0) {
        gl.disable(gl.BLEND);
      } else {
        gl.blendFunc(gl.ONE, gl.ONE_MINUS_SRC_ALPHA);
        gl.enable(gl.BLEND);
      }
      drawDisplay(target, now);
    }

    function drawDisplay(target, now) {
      let width = target == null ? gl.drawingBufferWidth : target.width;
      let height = target == null ? gl.drawingBufferHeight : target.height;
      displayMaterial.bind();
      if (displayProgram !== displayMaterial.activeProgram) {
        invalidateDisplayUniformCache();
        displayProgram = displayMaterial.activeProgram;
      }

      const uniforms = displayMaterial.uniforms;
      if (
        config.SHADING &&
        uniforms.texelSize &&
        (cachedTexelWidth !== width || cachedTexelHeight !== height)
      ) {
        gl.uniform2f(
          uniforms.texelSize,
          1.0 / width,
          1.0 / height,
        );
        cachedTexelWidth = width;
        cachedTexelHeight = height;
      }
      if (uniforms.uTexture) gl.uniform1i(uniforms.uTexture, dye.read.attach(0));

      const palette = paletteRef.current;
      if (palette !== cachedPalette) {
        if (uniforms.uFrontBase)
          gl.uniform3f(
            uniforms.uFrontBase,
            palette.frontBase[0],
            palette.frontBase[1],
            palette.frontBase[2],
          );
        if (uniforms.uFrontTint)
          gl.uniform3f(
            uniforms.uFrontTint,
            palette.frontTint[0],
            palette.frontTint[1],
            palette.frontTint[2],
          );
        if (uniforms.uBackBase)
          gl.uniform3f(
            uniforms.uBackBase,
            palette.backBase[0],
            palette.backBase[1],
            palette.backBase[2],
          );
        if (uniforms.uBackTint)
          gl.uniform3f(
            uniforms.uBackTint,
            palette.backTint[0],
            palette.backTint[1],
            palette.backTint[2],
          );
        if (uniforms.uFrontSeed)
          gl.uniform1f(uniforms.uFrontSeed, palette.frontSeed || 0);
        if (uniforms.uBackSeed)
          gl.uniform1f(uniforms.uBackSeed, palette.backSeed || 0);
        if (uniforms.uFrontKind)
          gl.uniform1f(uniforms.uFrontKind, palette.frontKind || 0);
        if (uniforms.uBackKind)
          gl.uniform1f(uniforms.uBackKind, palette.backKind || 0);
        if (uniforms.uFrontTextEnabled)
          gl.uniform1f(
            uniforms.uFrontTextEnabled,
            palette.frontTextEnabled ? 1 : 0,
          );
        if (uniforms.uBackTextEnabled)
          gl.uniform1f(
            uniforms.uBackTextEnabled,
            palette.backTextEnabled ? 1 : 0,
          );
        if (uniforms.uFrontTextColor && palette.frontTextColor)
          gl.uniform3f(
            uniforms.uFrontTextColor,
            palette.frontTextColor[0],
            palette.frontTextColor[1],
            palette.frontTextColor[2],
          );
        if (uniforms.uBackTextColor && palette.backTextColor)
          gl.uniform3f(
            uniforms.uBackTextColor,
            palette.backTextColor[0],
            palette.backTextColor[1],
            palette.backTextColor[2],
          );
        cachedPalette = palette;
      }

      if (uniforms.uTime) gl.uniform1f(uniforms.uTime, now * 0.001);
      if (
        uniforms.uResolution &&
        (cachedResolutionWidth !== width || cachedResolutionHeight !== height)
      ) {
        gl.uniform2f(
          uniforms.uResolution,
          width || 1,
          height || 1,
        );
        cachedResolutionWidth = width;
        cachedResolutionHeight = height;
      }
      if (
        uniforms.uNameMarkReady &&
        cachedNameMarkReady !== nameMarkState.ready
      ) {
        gl.uniform1f(uniforms.uNameMarkReady, nameMarkState.ready);
        cachedNameMarkReady = nameMarkState.ready;
      }
      if (
        uniforms.uNameMarkAspect &&
        cachedNameMarkAspect !== nameMarkState.aspect
      ) {
        gl.uniform1f(uniforms.uNameMarkAspect, nameMarkState.aspect);
        cachedNameMarkAspect = nameMarkState.aspect;
      }
      if (uniforms.uNameMark && nameMarkState.texture) {
        const unit = 2;
        gl.activeTexture(gl.TEXTURE0 + unit);
        gl.bindTexture(gl.TEXTURE_2D, nameMarkState.texture);
        if (!nameMarkSamplerSet) {
          gl.uniform1i(uniforms.uNameMark, unit);
          nameMarkSamplerSet = true;
        }
      }

      // Smash animation uniforms
      if (uniforms.uSmashAtlasReady && cachedSmashReady !== smashState.ready) {
        gl.uniform1f(uniforms.uSmashAtlasReady, smashState.ready);
        cachedSmashReady = smashState.ready;
      }
      if (
        uniforms.uSmashAtlasAspect &&
        cachedSmashAspect !== smashState.aspect
      ) {
        gl.uniform1f(uniforms.uSmashAtlasAspect, smashState.aspect);
        cachedSmashAspect = smashState.aspect;
      }
      if (
        uniforms.uSmashAtlasTexel &&
        (cachedSmashTexelX !== smashState.texelX ||
          cachedSmashTexelY !== smashState.texelY)
      ) {
        gl.uniform2f(
          uniforms.uSmashAtlasTexel,
          smashState.texelX,
          smashState.texelY,
        );
        cachedSmashTexelX = smashState.texelX;
        cachedSmashTexelY = smashState.texelY;
      }
      if (
        uniforms.uSmashFrameCount &&
        cachedSmashFrameCount !== SMASH_FRAME_COUNT
      ) {
        gl.uniform1f(uniforms.uSmashFrameCount, SMASH_FRAME_COUNT);
        cachedSmashFrameCount = SMASH_FRAME_COUNT;
      }
      if (
        uniforms.uSmashFrameColumns &&
        cachedSmashFrameColumns !== SMASH_FRAME_COLUMNS
      ) {
        gl.uniform1f(uniforms.uSmashFrameColumns, SMASH_FRAME_COLUMNS);
        cachedSmashFrameColumns = SMASH_FRAME_COLUMNS;
      }
      if (
        uniforms.uSmashFrameRows &&
        cachedSmashFrameRows !== SMASH_FRAME_ROWS
      ) {
        gl.uniform1f(uniforms.uSmashFrameRows, SMASH_FRAME_ROWS);
        cachedSmashFrameRows = SMASH_FRAME_ROWS;
      }
      if (uniforms.uSmashFps && cachedSmashFps !== SMASH_FPS) {
        gl.uniform1f(uniforms.uSmashFps, SMASH_FPS);
        cachedSmashFps = SMASH_FPS;
      }
      if (uniforms.uSmashAtlas && smashState.texture) {
        const unit = 4;
        gl.activeTexture(gl.TEXTURE0 + unit);
        gl.bindTexture(gl.TEXTURE_2D, smashState.texture);
        if (!smashSamplerSet) {
          gl.uniform1i(uniforms.uSmashAtlas, unit);
          smashSamplerSet = true;
        }
      }

      const transition = transitionRef.current;
      const aspect = width / height;
      let transitionActive = 0;
      let transitionRadius = 0;
      let transitionCenter = transition.center || { x: 0.5, y: 0.5 };

      if (transition.active) {
        const progress = Math.min(
          1,
          Math.max(0, (now - transition.startTime) / transition.duration),
        );
        const eased = 1 - Math.pow(1 - progress, 3);
        transitionRadius = transition.maxRadius * eased;
        transitionActive = 1;
        if (progress >= 1) {
          transition.active = false;
          transition.hold = true;
        }
      }
      if (transition.hold) {
        transitionRadius = transition.maxRadius;
        transitionActive = 1;
      }

      if (
        uniforms.uTransitionCenter &&
        (cachedTransitionCenterX !== transitionCenter.x ||
          cachedTransitionCenterY !== transitionCenter.y)
      ) {
        gl.uniform2f(
          uniforms.uTransitionCenter,
          transitionCenter.x,
          transitionCenter.y,
        );
        cachedTransitionCenterX = transitionCenter.x;
        cachedTransitionCenterY = transitionCenter.y;
      }
      if (
        uniforms.uTransitionRadius &&
        cachedTransitionRadius !== transitionRadius
      ) {
        gl.uniform1f(uniforms.uTransitionRadius, transitionRadius);
        cachedTransitionRadius = transitionRadius;
      }
      if (
        uniforms.uTransitionActive &&
        cachedTransitionActive !== transitionActive
      ) {
        gl.uniform1f(uniforms.uTransitionActive, transitionActive);
        cachedTransitionActive = transitionActive;
      }
      if (uniforms.uAspect && cachedAspect !== aspect) {
        gl.uniform1f(uniforms.uAspect, aspect);
        cachedAspect = aspect;
      }
      const alphaModeValue = alphaModeRef.current;
      if (uniforms.uAlphaMode && cachedAlphaMode !== alphaModeValue) {
        gl.uniform1f(uniforms.uAlphaMode, alphaModeValue);
        cachedAlphaMode = alphaModeValue;
      }
      const surface = surfaceRef.current;
      if (surface !== cachedSurface) {
        if (uniforms.uBandStrength)
          gl.uniform1f(uniforms.uBandStrength, surface.bandStrength);
        if (uniforms.uGrainStrength)
          gl.uniform1f(uniforms.uGrainStrength, surface.grainStrength);
        cachedSurface = surface;
      }
      blit(target);
    }

    function splatPointer(pointer) {
      const deltaX = pointer.texcoordX - pointer.prevTexcoordX;
      const deltaY = pointer.texcoordY - pointer.prevTexcoordY;
      const distance = Math.hypot(deltaX * canvas.width, deltaY * canvas.height);
      const steps = Math.min(8, Math.max(1, Math.floor(distance / 12)));
      const dx = (pointer.deltaX * config.SPLAT_FORCE) / steps;
      const dy = (pointer.deltaY * config.SPLAT_FORCE) / steps;

      for (let i = 1; i <= steps; i++) {
        const t = i / steps;
        const x = pointer.prevTexcoordX + deltaX * t;
        const y = pointer.prevTexcoordY + deltaY * t;
        splat(x, y, dx, dy, pointer.color);
      }
    }

    function clickSplat(pointer) {
      const color = generateColor();
      color.r *= 2.5;
      color.g *= 2.5;
      color.b *= 2.5;
      let dx = 10 * (Math.random() - 0.5);
      let dy = 30 * (Math.random() - 0.5);
      splat(pointer.texcoordX, pointer.texcoordY, dx, dy, color);
    }

    function splat(x, y, dx, dy, color) {
      splatProgram.bind();
      gl.uniform1i(splatProgram.uniforms.uTarget, velocity.read.attach(0));
      gl.uniform1f(
        splatProgram.uniforms.aspectRatio,
        canvas.width / canvas.height,
      );
      gl.uniform2f(splatProgram.uniforms.point, x, y);
      gl.uniform3f(splatProgram.uniforms.color, dx, dy, 0.0);
      gl.uniform1f(
        splatProgram.uniforms.radius,
        correctRadius(config.SPLAT_RADIUS / 100.0),
      );
      blit(velocity.write);
      velocity.swap();

      gl.uniform1i(splatProgram.uniforms.uTarget, dye.read.attach(0));
      gl.uniform3f(splatProgram.uniforms.color, color.r, color.g, color.b);
      blit(dye.write);
      dye.swap();
    }

    function correctRadius(radius) {
      let aspectRatio = canvas.width / canvas.height;
      if (aspectRatio > 1) radius *= aspectRatio;
      return radius;
    }

    function updatePointerDownData(pointer, id, posX, posY) {
      pointer.id = id;
      pointer.down = true;
      pointer.moved = false;
      pointer.texcoordX = posX / canvas.width;
      pointer.texcoordY = 1.0 - posY / canvas.height;
      pointer.prevTexcoordX = pointer.texcoordX;
      pointer.prevTexcoordY = pointer.texcoordY;
      pointer.deltaX = 0;
      pointer.deltaY = 0;
      pointer.color = generateColor();
    }

    function updatePointerMoveData(pointer, posX, posY, color) {
      pointer.prevTexcoordX = pointer.texcoordX;
      pointer.prevTexcoordY = pointer.texcoordY;
      pointer.texcoordX = posX / canvas.width;
      pointer.texcoordY = 1.0 - posY / canvas.height;
      pointer.deltaX = correctDeltaX(pointer.texcoordX - pointer.prevTexcoordX);
      pointer.deltaY = correctDeltaY(pointer.texcoordY - pointer.prevTexcoordY);
      pointer.moved =
        Math.abs(pointer.deltaX) > 0 || Math.abs(pointer.deltaY) > 0;
      pointer.color = color;
    }

    function updatePointerUpData(pointer) {
      pointer.down = false;
    }

    function correctDeltaX(delta) {
      let aspectRatio = canvas.width / canvas.height;
      if (aspectRatio < 1) delta *= aspectRatio;
      return delta;
    }

    function correctDeltaY(delta) {
      let aspectRatio = canvas.width / canvas.height;
      if (aspectRatio > 1) delta /= aspectRatio;
      return delta;
    }

    function generateColor() {
      return { r: 1, g: 1, b: 1 };
    }

    function wrap(value, min, max) {
      const range = max - min;
      if (range === 0) return min;
      return ((value - min) % range) + min;
    }

    function getResolution(resolution) {
      let aspectRatio = gl.drawingBufferWidth / gl.drawingBufferHeight;
      if (aspectRatio < 1) aspectRatio = 1.0 / aspectRatio;
      const min = Math.round(resolution);
      const max = Math.round(resolution * aspectRatio);
      if (gl.drawingBufferWidth > gl.drawingBufferHeight)
        return { width: max, height: min };
      else return { width: min, height: max };
    }

    function scaleByPixelRatio(input) {
      return Math.floor(input * pixelRatio);
    }

    function hashCode(s) {
      if (s.length === 0) return 0;
      let hash = 0;
      for (let i = 0; i < s.length; i++) {
        hash = (hash << 5) - hash + s.charCodeAt(i);
        hash |= 0;
      }
      return hash;
    }

    function triggerTransition({ x, y, duration = 900 } = {}) {
      resetFluid();
      const posX = scaleByPixelRatio(
        typeof x === "number" ? x : canvas.clientWidth / 2,
      );
      const posY = scaleByPixelRatio(
        typeof y === "number" ? y : canvas.clientHeight / 2,
      );
      const centerX = posX / canvas.width;
      const centerY = 1.0 - posY / canvas.height;
      const aspect = canvas.width / canvas.height;
      const corners = [
        { x: 0, y: 0 },
        { x: 1, y: 0 },
        { x: 0, y: 1 },
        { x: 1, y: 1 },
      ];
      const maxRadius = corners.reduce((max, corner) => {
        const dx = (corner.x - centerX) * aspect;
        const dy = corner.y - centerY;
        return Math.max(max, Math.hypot(dx, dy));
      }, 0);

      transitionRef.current = {
        active: true,
        startTime: performance.now(),
        duration: Math.max(150, duration),
        center: { x: centerX, y: centerY },
        maxRadius,
        hold: false,
      };
    }

    transitionTriggerRef.current = triggerTransition;

    // Named event handlers for proper cleanup
    function handleMouseDown(e) {
      let pointer = pointers[0];
      let posX = scaleByPixelRatio(e.clientX);
      let posY = scaleByPixelRatio(e.clientY);
      updatePointerDownData(pointer, -1, posX, posY);
      if (enableClickSplat) clickSplat(pointer);
    }

    let firstMouseMoveHandled = false;
    function handleMouseMove(e) {
      let pointer = pointers[0];
      let posX = scaleByPixelRatio(e.clientX);
      let posY = scaleByPixelRatio(e.clientY);
      if (!firstMouseMoveHandled) {
        let color = generateColor();
        updatePointerMoveData(pointer, posX, posY, color);
        firstMouseMoveHandled = true;
      } else {
        updatePointerMoveData(pointer, posX, posY, pointer.color);
      }
    }

    function handleTouchStart(e) {
      const touches = e.targetTouches;
      let pointer = pointers[0];
      for (let i = 0; i < touches.length; i++) {
        let posX = scaleByPixelRatio(touches[i].clientX);
        let posY = scaleByPixelRatio(touches[i].clientY);
        updatePointerDownData(pointer, touches[i].identifier, posX, posY);
      }
    }

    function handleTouchMove(e) {
      const touches = e.targetTouches;
      let pointer = pointers[0];
      for (let i = 0; i < touches.length; i++) {
        let posX = scaleByPixelRatio(touches[i].clientX);
        let posY = scaleByPixelRatio(touches[i].clientY);
        updatePointerMoveData(pointer, posX, posY, pointer.color);
      }
    }

    function handleTouchEnd(e) {
      const touches = e.changedTouches;
      let pointer = pointers[0];
      for (let i = 0; i < touches.length; i++) {
        updatePointerUpData(pointer);
      }
    }

    function handleWindowResize() {
      needsResize = true;
    }

    function handleVisibilityChange() {
      isDocumentHidden = document.hidden;
      if (isDocumentHidden) {
        if (animationFrameId.current) {
          cancelAnimationFrame(animationFrameId.current);
          animationFrameId.current = null;
        }
        return;
      }
      lastUpdateTime = performance.now();
      needsResize = true;
      if (!animationFrameId.current) {
        animationFrameId.current = requestAnimationFrame(updateFrame);
      }
    }

    // Add event listeners
    window.addEventListener("mousedown", handleMouseDown, { passive: true });
    window.addEventListener("mousemove", handleMouseMove, { passive: true });
    window.addEventListener("touchstart", handleTouchStart, { passive: true });
    window.addEventListener("touchmove", handleTouchMove, { passive: true });
    window.addEventListener("touchend", handleTouchEnd, { passive: true });
    window.addEventListener("resize", handleWindowResize, { passive: true });
    document.addEventListener("visibilitychange", handleVisibilityChange);

    if (!isDocumentHidden) {
      animationFrameId.current = requestAnimationFrame(updateFrame);
    }

    // Cleanup function
    return () => {
      isActive = false;
      transitionTriggerRef.current = null;
      resetRef.current = null;

      // Cancel animation frame
      if (animationFrameId.current) {
        cancelAnimationFrame(animationFrameId.current);
        animationFrameId.current = null;
      }

      // Remove event listeners
      window.removeEventListener("mousedown", handleMouseDown);
      window.removeEventListener("mousemove", handleMouseMove);
      window.removeEventListener("touchstart", handleTouchStart);
      window.removeEventListener("touchmove", handleTouchMove);
      window.removeEventListener("touchend", handleTouchEnd);
      window.removeEventListener("resize", handleWindowResize);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      if (resizeObserver) resizeObserver.disconnect();

      if (nameMarkState.texture) {
        gl.deleteTexture(nameMarkState.texture);
        nameMarkState.texture = null;
      }
      if (smashState.texture) {
        gl.deleteTexture(smashState.texture);
        smashState.texture = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className={`splash-cursor ${className}`.trim()}>
      <canvas ref={canvasRef} className="splash-cursor__canvas" />
    </div>
  );
});

export default SplashCursor;
